/*
============================================================================
V600.42 DIRECT ROUTE BUTTON AUTHORITY
- Route Here calls DriveIQAdventureRoute.startRouteTo() directly.
- Removes the duplicate state.requestRoute + route:requested event lanes.
- Prevents older shell/native listeners from winning and sending users Home.
============================================================================
*/

/* DRIVE IQ — ADVENTURE MODE V600.44 WEBSITE IQ
   - Adds a conditional, safe View Website link beneath Route Here and quick actions.
   - No navigation, scan, photo, voice or result flow changes.

   DRIVE IQ — ADVENTURE MODE V600.43 PREMIUM PLACE IQ PRESENTATION
   File: adventure-place-card.js

   V600.43 premium Place IQ presentation:
   - Restyles the selected-place card to match the new Adventure mockup direction.
   - Adds a restrained Drive IQ Pick badge beside the real category label.
   - Strengthens hero, title, verdict, recommendation, route, and quick-action hierarchy.
   - Hides the floating Places pill while Place IQ owns the screen.
   - Preserves immutable selected-place identity, verified photo authority, Save, Share,
     Listen, direct Route Here authority, transactional opening, rollback, and shelf restore.

   V600.41 focused polish:
   - Reframes missing imagery as an intentional Drive IQ Preview instead of an error.
   - Hides gallery counters, dots, and navigation until a real photo is verified.
   - Never displays a meaningless 1 / 1 counter for fallback-only or single-photo heroes.
   - Adds a deeper cinematic fade and smoother verified-photo transition.
   - Expands category-aware premium preview artwork for dessert, shopping, and historic places.
   - Preserves V600.40 verified-photo authority, immutable selection, routing, Share IQ, Listen, and rollback behavior.

   V600.40 focused repair:
   - Paints a premium category-aware hero fallback before any remote photo request begins.
   - Never renders an unverified remote photo URL directly into the initial Place IQ markup.
   - Verifies remote hero photos with an Image load probe before treating them as usable.
   - Restores the branded fallback when a photo is empty, blocked, quota-limited, or fails to decode.
   - Rechecks delayed Photo IQ hydration so a late broken URL cannot leave a black hero.
   - Applies the same verified-photo authority when swiping the Place IQ gallery.
   - Preserves immutable selection, routing, gallery, Share IQ, Listen, and rollback behavior.

   V600.39 focused repair:
   - Carries the exact tapped result object into Place IQ without re-resolving it by mutable array position.
   - Creates one stable selection snapshot before render, state, camera, photo, share, and Route Here handoff.
   - Prefers embedded pin/result payloads over secondary results.items lookups.
   - Uses results.items lookup only as a legacy last resort for key-only Mapbox features.
   - Prevents a photo resolver changing placeId from changing the selected-place identity mid-open.
   - Rejects stale duplicate selection events that do not match the active selection transaction.
   - Preserves all V600.38 gallery, Share IQ, Listen, rollback, and routing behavior.

   Adds:
   - Swipeable Google photo gallery using Photo IQ gallery bundles.
   - Image dots, counter, previous/next controls, and touch swiping.
   - Rating, review count, open/closed, distance, and decision chips when present.
   - Save and Share actions.
   - Immutable selected-place authority for Route Here.
   - Exact map focus and clean shelf restoration.
   - Preserve the proven V600.25 pin-opening path.
   - Fully reset selection state on close.
   - Paint a deliberate hero fallback while Photo IQ resolves.
   - Opens Place IQ transactionally and verifies the card before hiding results.
   - Rolls back immediately if rendering, state, camera, or event handoff fails.
   - Never leaves the app in a black Place IQ state.
   - Removes the retired #diqAdventureDestResults search ID from shelf handling.
   - Adds a cinematic Place IQ decision header with distance, open status, rating, and confidence.
   - Generates category-aware decision chips such as Quick Stop, Easy Parking, Local Favorite, Great Workspace, and Mobile Pickup.
   - Rewrites generic recommendation text into concise opinion-engine language.
   - Adds a dedicated Why This Place section without changing proven routing or selection authority.
   - Keeps Save, Share, Route Here, transactional opening, and rollback behavior intact.
   - Adds an explicit LISTEN control that speaks the same Recommendation Intelligence shown on the card.
   - Uses browser speech synthesis with no backend or API cost.
   - Stops speech on card close, new-place open, Route Here, rollback, page hide, or a second LISTEN tap.
   - Prevents overlapping utterances and keeps button state synchronized with playback.
   - Never speaks automatically; Place IQ voice always requires a user action.
   - Evolves Recommendation IQ from metadata narration into a concise travel-companion voice.
   - Uses stable category-aware language so each place feels personal without changing on every render.
   - Rejects weak fallback phrases such as Very close, Nearby, Strong match, and Worth considering.
   - Adds destination, local, iconic, quick-stop, atmosphere, scenic, and experience-aware phrasing.
   - Keeps spoken summaries brief, natural, and decision-focused.
   - Adds dedicated Water / Waterfront place intelligence for beaches, marinas,
     boardwalks, piers, lakes, rivers, and waterfront attractions.
   - Replaces generic waterfront fallback copy with destination-aware reasons.
   - Adds scenic, quieter beach, family-friendly, sunset, swimming, marina,
     boardwalk, fishing, and waterfront-experience chips when supported.
   - Moves the Places pill to the top-center while Place IQ is open so it no
     longer covers the hero photo or card controls.
   - Restores the pill to its normal map position when Place IQ closes.
   - Matches Provider V600.16 social-proof fields for ratings, rating count,
     open status, Google Maps links, and future review highlights.
   - Adds a compact Drive IQ Verdict banner above the recommendation.
   - Formats Google social proof as “★ 4.8 · 18.4K Google ratings.”
   - Keeps Google evidence secondary to Drive IQ judgment.
   - Prevents review arrays from being mistaken for numeric review counts.
   - Adds concise social-proof validation beneath Why This Place.
   - Preserves the existing transactional open, Listen, Save, Share, Route Here,
     waterfront opinion logic, and top-centered Places pill.
   - Tightens the top information stack so the place name, address, and verdict
     read as one premium recommendation flow.
   - Promotes Drive IQ judgment ahead of supporting metadata.
   - Renames “Why This Place” to “Why Drive IQ Picked This.”
   - Adds a compact, category-aware “Worth Knowing” intelligence block.
   - Keeps Google social proof as supporting validation beneath Drive IQ guidance.
   - Removes weak confidence labels such as Strong Match when a stronger verdict
     is already present.
   - Adds subtle, bounded card entrance motion without touching routing or state.
   - Deepens the hero image and adds a cinematic fade into the card body.
   - Standardizes premium corner radii and spacing rhythm across the card.
   - Strengthens the Drive IQ verdict presentation without changing its logic.
   - Keeps Google validation visually secondary to Drive IQ judgment.
   - Adds a compact Review Highlights IQ section when provider review data exists.
   - Limits review highlights to two short attributed excerpts.
   - Never shows a long review feed on the main Place IQ card.
   - Preserves all transactional opening, rollback, Listen, Save, Share, gallery,
     routing, and top-centered Places pill behavior.
   - Consumes Provider V600.17 and Scan V600.51 Event IQ fields.
   - Adds a dedicated Tonight IQ experience block near the top of Place IQ.
   - Distinguishes Confirmed Tonight, Upcoming Event, Usually Happens Tonight,
     and Known Music Venue without overstating certainty.
   - Shows event time, venue area/floor, source freshness, and recurrence details.
   - Moves Route Here directly beneath the recommendation/event proof stack.
   - Makes scrolling optional for supporting intelligence instead of required
     to begin navigation.
   - Keeps Save, Share, and Listen directly below the primary route action.
   - Removes the duplicate Route Here button from the bottom of the card.
   - Adds natural singular/plural distance grammar for Place IQ voice.
   - Replaces decimal-heavy distances with conversational quarter-mile and half-mile phrasing.
   - Adds spoken-only pronunciation overrides for commonly misread destinations and roads.
   - Prefers natural premium English voices when the device provides them.
   - Uses calmer rate, pitch, volume, and sentence pacing for less robotic delivery.
   - Keeps visible Place IQ text unchanged while improving only the spoken version.
   - Replaces generic Adventure Mode URL sharing with exact place-specific Drive IQ links.
   - Preserves canonical Place ID, name, address, latitude, and longitude in every shared link.
   - Uses the native device share sheet when available and clipboard fallback otherwise.
   - Supports Meet Me Here behavior for exact coordinate-only pins.
   - Never copies raw Adventure Mode source or page code as the shared destination.
   - Emits Share IQ events with the exact immutable selected-place payload.
*/

(function(window,document){
"use strict";

if(window.DriveIQAdventurePlaceCard){return;}

var cfg=window.DriveIQAdventureConfig;
var utils=window.DriveIQAdventureUtils;
var state=window.DriveIQAdventureState;
var events=window.DriveIQAdventureEvents;
var mapApi=window.DriveIQAdventureMap;

var initialized=false;
var card=null;
var activeItem=null;
var opening=false;
var selectionGeneration=0;
var activeSelectionToken="";
var committedSelectionByToken={};
var heroHydrationTimer=null;
var heroRecoveryTimer=null;
var galleryIndex=0;
var eventBindingsInstalled=false;
var initRetryTimer=null;
var initRetryCount=0;
var MAX_INIT_RETRIES=80;
var hiddenShelfState=[];
var touchStartX=null;
var touchStartY=null;
var activeUtterance=null;
var speaking=false;
var spokenPlaceKey="";
var FAVORITES_KEY=
  cfg.storage&&cfg.storage.favoriteStops
    ? cfg.storage.favoriteStops
    : "diq_place_iq_favorite_stops_v1";

if(!cfg||!utils||!state||!events||!mapApi){
  throw new Error("Place Card requires config/utils/state/events/map.");
}

function clean(value){
  return String(value==null?"":value).replace(/\s+/g," ").trim();
}

function numberValue(value){
  var n=Number(value);
  return isFinite(n)?n:null;
}

function stableSelectionIdentity(item){
  if(!item){return "";}

  return clean([
    item.__adventureKey||"",
    item.placeId||
      item.place_id||
      item.googlePlaceId||
      item.google_place_id||
      item.id||
      "",
    item.label||item.name||item.title||"",
    item.lat!=null?Number(item.lat).toFixed(6):"",
    item.lng!=null?Number(item.lng).toFixed(6):""
  ].join("|"));
}

function cloneSelectionItem(value){
  var item=value;
  var clone;
  var keys;
  var index;
  var key;

  if(!item||typeof item!=="object"){return item||null;}

  clone={};
  keys=Object.keys(item);

  for(index=0;index<keys.length;index+=1){
    key=keys[index];

    if(Array.isArray(item[key])){
      clone[key]=item[key].slice();
    }else if(
      item[key]&&
      typeof item[key]==="object"&&
      (
        key==="primaryEvent"||
        key==="destination"||
        key==="metadata"
      )
    ){
      clone[key]=Object.assign({},item[key]);
    }else{
      clone[key]=item[key];
    }
  }

  clone.__diqSelectionIdentity=
    item.__diqSelectionIdentity||
    stableSelectionIdentity(item);
  clone.__diqSelectionCapturedAt=Date.now?Date.now():(new Date()).getTime();

  return clone;
}

function selectionToken(item,source){
  selectionGeneration+=1;

  return [
    "place-selection",
    String(selectionGeneration),
    stableSelectionIdentity(item),
    String(source||"unknown")
  ].join("|");
}

function embeddedSelectionItem(value){
  var source=value;
  var candidates=[];
  var index;
  var candidate;

  if(!source||typeof source!=="object"){return null;}

  candidates=[
    source.__diqExactItem,
    source.__diqPlaceItem,
    source.selectedPlace,
    source.item,
    source.place,
    source.result,
    source.feature&&source.feature.__diqExactItem,
    source.feature&&source.feature.properties&&source.feature.properties.__diqExactItem,
    source.payload&&source.payload.__diqExactItem,
    source.payload&&source.payload.__diqPlaceItem,
    source.payload&&source.payload.selectedPlace,
    source.payload&&source.payload.item,
    source.payload&&source.payload.place,
    source.payload&&source.payload.result,
    source.detail&&source.detail.__diqExactItem,
    source.detail&&source.detail.__diqPlaceItem,
    source.detail&&source.detail.selectedPlace,
    source.detail&&source.detail.item,
    source.detail&&source.detail.place,
    source.detail&&source.detail.result
  ];

  for(index=0;index<candidates.length;index+=1){
    candidate=candidates[index];

    if(
      candidate&&
      typeof candidate==="object"&&
      isFinite(Number(candidate.lat))&&
      isFinite(Number(candidate.lng))
    ){
      return candidate;
    }
  }

  return null;
}

function findResultByKey(key){
  var results=state.get("results.items",[])||[];
  var index;

  if(!key){return null;}

  for(index=0;index<results.length;index+=1){
    if(placeKey(results[index])===String(key)){
      return results[index];
    }

    if(
      results[index].__adventureKey&&
      String(results[index].__adventureKey)===String(key)
    ){
      return results[index];
    }
  }

  return null;
}

function unwrapItem(value){
  var exact=embeddedSelectionItem(value);
  var item=value;
  var depth=0;
  var featureKey;
  var resolved;

  /*
    V600.39 Selection Authority:
    If the event already carries the tapped result object, preserve that exact
    object. Never throw it away and then resolve it again from a mutable pool.
  */
  if(exact){
    return exact;
  }

  while(item&&depth<8){
    depth+=1;

    if(
      item.payload!==undefined&&
      item.payload!==item
    ){
      item=item.payload;
      exact=embeddedSelectionItem(item);
      if(exact){return exact;}
      continue;
    }

    if(
      item.detail!==undefined&&
      item.detail!==item
    ){
      item=item.detail;
      exact=embeddedSelectionItem(item);
      if(exact){return exact;}
      continue;
    }

    if(item.feature&&item.feature!==item){
      item=item.feature;
      exact=embeddedSelectionItem(item);
      if(exact){return exact;}
      continue;
    }

    break;
  }

  if(
    item&&
    item.properties&&
    (
      item.properties.key||
      item.properties.__adventureKey
    )
  ){
    featureKey=
      item.properties.key||
      item.properties.__adventureKey;

    /*
      Key-only feature payloads are legacy compatibility only. This lookup is
      intentionally the last resort, never the primary selection path.
    */
    resolved=findResultByKey(featureKey);
    if(resolved){return resolved;}
  }

  if(
    item&&
    !isFinite(Number(item.lat))&&
    item.geometry&&
    item.geometry.type==="Point"&&
    Array.isArray(item.geometry.coordinates)
  ){
    item={
      label:
        item.properties&&(
          item.properties.name||
          item.properties.label
        )||"Adventure Place",
      name:
        item.properties&&(
          item.properties.name||
          item.properties.label
        )||"Adventure Place",
      placeId:
        item.properties&&item.properties.placeId||"",
      __adventureKey:
        item.properties&&(
          item.properties.__adventureKey||
          item.properties.key
        )||"",
      lat:Number(item.geometry.coordinates[1]),
      lng:Number(item.geometry.coordinates[0])
    };
  }

  return item||null;
}

function validItem(value){
  var item=unwrapItem(value);

  if(!item){return null;}

  if(!isFinite(Number(item.lat))||!isFinite(Number(item.lng))){
    return null;
  }

  return item;
}

function getCard(){
  if(!card){
    card=utils.byId(cfg.placeCard.cardID);
  }

  return card;
}

function escape(value){
  return utils.escapeHTML(String(value==null?"":value));
}

function placeKey(item){
  try{
    if(
      window.DriveIQAdventurePhoto&&
      typeof window.DriveIQAdventurePhoto.placeKey==="function"
    ){
      return window.DriveIQAdventurePhoto.placeKey(item);
    }
  }catch(error){}

  return [
    item&&(
      item.placeId||
      item.place_id||
      item.googlePlaceId||
      item.id||
      item.label||
      item.name||
      ""
    ),
    item&&item.lat,
    item&&item.lng
  ].join("|");
}

function ensureStyles(){
  if(document.getElementById("diqV600PlaceIQStyles")){return;}

  var style=document.createElement("style");
  style.id="diqV600PlaceIQStyles";
  style.textContent=
    "#diqAdventureInfoCard{"+
      "display:none;position:fixed;left:18px;right:18px;"+
      "bottom:calc(18px + env(safe-area-inset-bottom,0px));"+
      "z-index:120000;max-height:78dvh;overflow:auto;"+
      "-webkit-overflow-scrolling:touch;border-radius:24px;"+
      "background:rgba(3,11,20,.985);"+
      "border:1px solid rgba(49,255,198,.42);"+
      "box-shadow:0 24px 70px rgba(0,0,0,.65);"+
      "pointer-events:auto;transform:translateY(14px) scale(.985);"+
      "opacity:0;transition:transform .22s ease,opacity .22s ease;"+
    "}"+
    "#diqAdventureInfoCard.diqPlaceIQOpen{display:block!important;"+
      "visibility:visible!important;opacity:1!important;pointer-events:auto!important;"+
      "transform:translateY(0) scale(1)!important;}"+
    ".diqV600PlaceCardInner{position:relative;}"+
    ".diqV600Gallery::after{content:\"\";position:absolute;left:0;right:0;bottom:0;"+
      "height:118px;z-index:4;pointer-events:none;"+
      "background:linear-gradient(to bottom,rgba(3,11,20,0) 0%,rgba(3,11,20,.18) 28%,"+
      "rgba(3,11,20,.58) 66%,rgba(3,11,20,.96) 100%);}"+
    ".diqV600Gallery{position:relative;height:260px;overflow:hidden;"+
      "border-radius:23px 23px 0 0;background:#10202c;}"+
    ".diq-v600-place-photo{position:absolute;inset:0;"+
      "background-position:center;background-size:cover;"+
      "background-repeat:no-repeat;transition:opacity .30s ease,transform .34s ease,filter .30s ease;"+
      "transform:scale(1.015);filter:saturate(.96);}"+
    ".diqV600Gallery.diqGalleryHasPhoto .diq-v600-place-photo{transform:scale(1.005);filter:none;}"+
    ".diq-v600-place-photo[data-photo-fallback='1']{"+
      "background-color:#10202c!important;}"+
    ".diq-v600-place-photo[data-photo-fallback='1']::before{"+
      "content:'DRIVE IQ PREVIEW';position:absolute;left:24px;top:24px;"+
      "padding:8px 11px;border-radius:999px;background:rgba(3,11,20,.46);"+
      "border:1px solid rgba(255,255,255,.14);color:rgba(255,255,255,.84);"+
      "box-shadow:0 8px 24px rgba(0,0,0,.18);"+
      "font:900 9px/1 system-ui;letter-spacing:1.15px;}"+
    ".diq-v600-place-photo[data-photo-fallback='1']::after{"+
      "content:attr(data-fallback-mark);position:absolute;left:24px;bottom:24px;"+
      "font:900 52px/1 system-ui;color:rgba(255,255,255,.92);"+
      "text-shadow:0 10px 30px rgba(0,0,0,.4);}"+
    ".diqV600PlaceClose{position:absolute;right:12px;top:12px;z-index:8;"+
      "width:42px;height:42px;border:1px solid rgba(255,255,255,.14);"+
      "border-radius:16px;background:rgba(5,14,24,.9);color:#fff;"+
      "font-size:26px;line-height:40px;text-align:center;}"+
    ".diqV600GalleryNav{display:none;position:absolute;top:50%;transform:translateY(-50%);"+
      "z-index:6;width:38px;height:38px;border:1px solid rgba(255,255,255,.16);"+
      "border-radius:13px;background:rgba(3,11,20,.76);color:#fff;"+
      "font:900 22px/36px system-ui;text-align:center;}"+
    ".diqV600Gallery.diqGalleryHasPhoto[data-gallery-multiple='1'] .diqV600GalleryNav{display:block;}"+
    ".diqV600GalleryPrev{left:10px}.diqV600GalleryNext{right:10px}"+
    ".diqV600GalleryMeta{display:none;position:absolute;left:12px;right:12px;bottom:10px;"+
      "z-index:7;display:flex;align-items:center;justify-content:space-between;"+
      "pointer-events:none;}"+
    ".diqV600Gallery.diqGalleryHasPhoto[data-gallery-multiple='1'] .diqV600GalleryMeta{display:flex;}"+
    ".diqV600GalleryDots{display:flex;gap:6px;}"+
    ".diqV600GalleryDot{width:7px;height:7px;border-radius:999px;"+
      "background:rgba(255,255,255,.44);box-shadow:0 2px 8px rgba(0,0,0,.35);}"+
    ".diqV600GalleryDot.on{width:18px;background:#47f2c3;}"+
    ".diqV600GalleryCount{padding:7px 9px;border-radius:999px;"+
      "background:rgba(3,11,20,.82);color:#fff;font:900 10px/1 system-ui;"+
      "letter-spacing:.7px;}"+
    ".diqV600PlaceBody{padding:14px 18px 20px;}"+
    ".diqV600PlaceEyebrow{color:#48f1c3;font:900 10px/1.2 system-ui;"+
      "letter-spacing:1.25px;text-transform:uppercase;}"+
    ".diqV600PlaceTitle{margin:6px 0 4px;color:#fff;"+
      "font:900 28px/1.08 system-ui;}"+
    ".diqV600PlaceAddress{color:#b8c7d1;font:600 14px/1.35 system-ui;margin-bottom:10px;}"+
    ".diqV600DecisionBar{display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:16px;}"+
    ".diqV600DecisionPill{display:inline-flex;align-items:center;gap:6px;padding:8px 10px;"+
      "border-radius:999px;background:rgba(255,255,255,.055);"+
      "border:1px solid rgba(255,255,255,.1);color:#eaf7fb;"+
      "font:850 11px/1 system-ui;}"+
    ".diqV600DecisionPill.good{color:#7fffd7;border-color:rgba(68,242,195,.34);"+
      "background:rgba(68,242,195,.08);}"+
    ".diqV600DecisionPill.warn{color:#ffd97e;border-color:rgba(255,217,126,.34);"+
      "background:rgba(255,217,126,.07);}"+
    ".diqV600DecisionPill.strong{color:#07121a;border:0;"+
      "background:linear-gradient(110deg,#ffe886,#ffbc55,#3ff0bc);}"+
    ".diqV600PlaceChips{display:flex;gap:8px;flex-wrap:wrap;margin-top:16px;}"+
    ".diqV600PlaceChip{padding:8px 10px;border-radius:999px;"+
      "background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.1);"+
      "color:#e9f4f8;font:800 11px/1 system-ui;}"+
    ".diqV600PlaceChip.good{color:#7fffd7;border-color:rgba(68,242,195,.34);}"+
    ".diqV600PlaceChip.warn{color:#ffd97e;border-color:rgba(255,217,126,.34);}"+
    ".diqV600WhyBlock{margin-top:18px;padding:17px;border-radius:20px;"+
      "background:linear-gradient(145deg,rgba(255,255,255,.055),rgba(59,240,191,.045));"+
      "border:1px solid rgba(59,240,191,.16);}"+
    ".diqV600WhyLabel{color:#48f1c3;font:900 9px/1 system-ui;"+
      "letter-spacing:1.25px;text-transform:uppercase;}"+
    ".diqV600PlaceReason{margin-top:7px;color:#f8e4a9;"+
      "font:850 14px/1.42 system-ui;}"+
    ".diqV600DecisionChips{display:flex;gap:8px;flex-wrap:wrap;margin-top:11px;}"+
    ".diqV600DecisionChip{padding:8px 10px;border-radius:999px;"+
      "background:rgba(255,255,255,.055);border:1px solid rgba(255,255,255,.1);"+
      "color:#dcebf1;font:800 10px/1 system-ui;}"+
    ".diqV600Verdict{display:flex;align-items:center;gap:12px;margin-top:10px;"+
      "padding:14px 16px;border-radius:18px;"+
      "background:linear-gradient(135deg,rgba(255,225,126,.12),rgba(61,239,190,.08));"+
      "border:1px solid rgba(255,220,120,.24);color:#fff;}"+
    ".diqV600VerdictIcon{font-size:22px;line-height:1;}"+
    ".diqV600VerdictCopy{display:grid;gap:4px;}"+
    ".diqV600VerdictLabel{color:#ffe18a;font:950 12px/1 system-ui;"+
      "letter-spacing:1.15px;text-transform:uppercase;}"+
    ".diqV600VerdictSub{color:#c8d5dc;font:750 12px/1.45 system-ui;}"+
    ".diqV600SocialProof{display:flex;align-items:center;gap:10px;margin-top:14px;"+
      "padding:12px 14px;border-radius:16px;background:rgba(255,255,255,.04);"+
      "border:1px solid rgba(255,255,255,.08);color:#f4f8fa;"+
      "font:850 12px/1.4 system-ui;}"+
    ".diqV600SocialProof strong{color:#ffd978;}"+
    ".diqV600SocialProofSource{margin-left:auto;color:#8fa4b2;"+
      "font:800 9px/1 system-ui;letter-spacing:.65px;text-transform:uppercase;}"+
    ".diqV600WorthKnowing{margin-top:16px;padding:16px 17px;border-radius:20px;"+
      "background:rgba(255,255,255,.035);border:1px solid rgba(255,255,255,.08);}"+
    ".diqV600WorthKnowingLabel{color:#a9c7d6;font:950 9px/1 system-ui;"+
      "letter-spacing:1.15px;text-transform:uppercase;}"+
    ".diqV600WorthKnowingList{display:grid;gap:8px;margin-top:10px;}"+
    ".diqV600WorthKnowingItem{display:flex;align-items:flex-start;gap:9px;"+
      "color:#dbe8ee;font:750 12px/1.4 system-ui;}"+
    ".diqV600WorthKnowingDot{flex:0 0 auto;width:6px;height:6px;margin-top:5px;"+
      "border-radius:999px;background:#48f1c3;box-shadow:0 0 12px rgba(72,241,195,.45);}"+
    ".diqV600ReviewHighlights{margin-top:16px;padding:16px 17px;border-radius:20px;"+
      "background:linear-gradient(145deg,rgba(255,255,255,.04),rgba(255,214,120,.035));"+
      "border:1px solid rgba(255,255,255,.08);}"+
    ".diqV600ReviewHighlightsLabel{color:#ffd978;font:950 9px/1 system-ui;"+
      "letter-spacing:1.15px;text-transform:uppercase;}"+
    ".diqV600ReviewList{display:grid;gap:12px;margin-top:11px;}"+
    ".diqV600ReviewItem{padding:12px 13px;border-radius:16px;background:rgba(255,255,255,.035);"+
      "border:1px solid rgba(255,255,255,.06);}"+
    ".diqV600ReviewQuote{color:#eaf2f6;font:760 12px/1.5 system-ui;}"+
    ".diqV600ReviewMeta{display:flex;justify-content:space-between;gap:10px;margin-top:8px;"+
      "color:#8fa4b2;font:800 9px/1.2 system-ui;}"+
    ".diqV600ReviewStars{color:#ffd978;letter-spacing:.8px;}"+
    ".diqV600TonightIQ{margin-top:14px;padding:16px 17px;border-radius:20px;"+
      "background:linear-gradient(145deg,rgba(255,214,120,.10),rgba(72,241,195,.06));"+
      "border:1px solid rgba(255,214,120,.24);box-shadow:0 14px 34px rgba(0,0,0,.18);}"+
    ".diqV600TonightIQ.confirmed{border-color:rgba(72,241,195,.34);"+
      "background:linear-gradient(145deg,rgba(72,241,195,.11),rgba(59,220,255,.06));}"+
    ".diqV600TonightIQHeader{display:flex;align-items:center;justify-content:space-between;gap:12px;}"+
    ".diqV600TonightIQBadge{display:inline-flex;align-items:center;gap:7px;"+
      "color:#ffe18a;font:950 10px/1 system-ui;letter-spacing:1px;text-transform:uppercase;}"+
    ".diqV600TonightIQ.confirmed .diqV600TonightIQBadge{color:#72ffd4;}"+
    ".diqV600TonightIQType{color:#9fb2bd;font:850 9px/1 system-ui;"+
      "letter-spacing:.75px;text-transform:uppercase;}"+
    ".diqV600TonightIQTitle{margin-top:11px;color:#fff;font:950 18px/1.15 system-ui;}"+
    ".diqV600TonightIQDetails{display:flex;gap:8px;flex-wrap:wrap;margin-top:10px;}"+
    ".diqV600TonightIQDetail{padding:8px 10px;border-radius:999px;"+
      "background:rgba(255,255,255,.055);border:1px solid rgba(255,255,255,.09);"+
      "color:#e8f2f6;font:850 10px/1 system-ui;}"+
    ".diqV600TonightIQSource{margin-top:10px;color:#9eb1bc;font:750 10px/1.35 system-ui;}"+
    ".diqV600PrimaryActions{margin-top:14px;}"+
    ".diqV600PrimaryActions .diqV600RouteHere{margin-top:0;}"+
    ".diqV600QuickActions{display:grid;grid-template-columns:repeat(3,1fr);gap:9px;margin-top:10px;}"+
    ".diqV600QuickAction{padding:12px 8px;border-radius:16px;border:1px solid rgba(59,220,255,.28);"+
      "background:rgba(5,17,28,.9);color:#fff;font:850 11px/1 system-ui;}"+
    "#diqAdventureInfoCard.diqPlaceIQOpen .diqV600Verdict{animation:diqVerdictIn .22s ease both;}"+
    "#diqAdventureInfoCard.diqPlaceIQOpen .diqV600WhyBlock{animation:diqWhyIn .26s ease both;}"+
    "#diqAdventureInfoCard.diqPlaceIQOpen .diqV600RouteHere{animation:diqRouteIn .30s ease both;}"+
    "@keyframes diqVerdictIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}"+
    "@keyframes diqWhyIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}"+
    "@keyframes diqRouteIn{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}"+
    ".diqV600ListenAction{display:flex;width:100%;align-items:center;justify-content:center;"+
      "gap:9px;margin-top:15px;padding:14px 12px;border-radius:16px;"+
      "border:1px solid rgba(72,241,195,.4);"+
      "background:linear-gradient(145deg,rgba(72,241,195,.11),rgba(59,220,255,.07));"+
      "color:#eafff9;font:900 13px/1 system-ui;letter-spacing:.08em;}"+
    ".diqV600ListenAction.is-speaking{color:#07121a;border-color:transparent;"+
      "background:linear-gradient(110deg,#ffe886,#ffbc55,#3ff0bc);}"+
    ".diqV600ListenIcon{font-size:17px;line-height:1;}"+
    ".diqV600PlaceActions{display:grid;grid-template-columns:1fr 1fr;gap:10px;"+
      "margin-top:10px;}"+
    ".diqV600SecondaryAction{padding:13px 12px;border-radius:18px;"+
      "border:1px solid rgba(59,220,255,.34);background:rgba(5,17,28,.9);"+
      "color:#fff;font:850 13px/1 system-ui;}"+
    ".diqV600RouteHere{display:block;width:100%;margin-top:16px;"+
      "padding:20px 17px;border:0;border-radius:18px;"+
      "background:linear-gradient(110deg,#ffe886,#ffbc55,#3ff0bc);"+
      "color:#07121a;font:900 16px/1 system-ui;letter-spacing:.7px;}"+
    "body.diqPlaceIQMode #diqV600ResultsPill,"+
    "body.diq-v600-place #diqV600ResultsPill{"+
      "left:50%!important;right:auto!important;"+
      "top:calc(74px + env(safe-area-inset-top,0px))!important;"+
      "transform:translateX(-50%) scale(.92)!important;"+
      "min-width:92px!important;height:40px!important;padding:0 15px!important;"+
      "z-index:2147483640!important;transition:left .22s ease,right .22s ease,"+
      "top .22s ease,transform .22s ease!important;"+
    "}"+
    "body.diqPlaceIQMode #diqAdventureResultsShelf,"+
        "body.diqPlaceIQMode #diqV519Shelf,"+
    "body.diqPlaceIQMode .diqAdventureResultsShelf,"+
    "body.diqPlaceIQMode .diqV600ResultsShelf,"+
    "body.diqPlaceIQMode [data-diq-results-shelf]{"+
      "display:none!important;visibility:hidden!important;opacity:0!important;"+
      "pointer-events:none!important;}";

  document.head.appendChild(style);
}


function ensurePremiumPlaceStyles(){
  if(document.getElementById("diqV601PremiumPlaceIQStyles")){return true;}

  var style=document.createElement("style");
  style.id="diqV601PremiumPlaceIQStyles";
  style.type="text/css";
  style.textContent=
    "#diqAdventureInfoCard{"+
      "left:14px!important;right:14px!important;"+
      "bottom:calc(14px + env(safe-area-inset-bottom,0px))!important;"+
      "max-height:82dvh!important;border-radius:30px!important;"+
      "background:radial-gradient(circle at 86% 0%,rgba(255,216,106,.07),transparent 30%),"+
      "linear-gradient(180deg,rgba(5,17,28,.992),rgba(2,10,18,.997))!important;"+
      "border:1px solid rgba(72,226,255,.15)!important;"+
      "box-shadow:0 30px 80px rgba(0,0,0,.58),0 1px 0 rgba(255,255,255,.045) inset!important;"+
      "backdrop-filter:blur(28px) saturate(1.08)!important;"+
      "-webkit-backdrop-filter:blur(28px) saturate(1.08)!important;"+
    "}"+
    ".diqV600PlaceCardInner{overflow:hidden;border-radius:30px;}"+
    ".diqV600Gallery{height:278px!important;border-radius:29px 29px 0 0!important;"+
      "background:#0b1c28!important;}"+
    ".diqV600Gallery::after{height:150px!important;"+
      "background:linear-gradient(to bottom,rgba(2,10,18,0) 0%,rgba(2,10,18,.10) 28%,"+
      "rgba(2,10,18,.58) 70%,rgba(2,10,18,.99) 100%)!important;}"+
    ".diq-v600-place-photo{transform:scale(1.018)!important;"+
      "filter:saturate(1.03) contrast(1.02)!important;}"+
    ".diqV600Gallery.diqGalleryHasPhoto .diq-v600-place-photo{transform:scale(1.008)!important;}"+
    ".diqV600PlaceClose{right:14px!important;top:14px!important;width:42px!important;height:42px!important;"+
      "border-radius:15px!important;background:rgba(3,13,22,.84)!important;"+
      "border:1px solid rgba(255,255,255,.14)!important;"+
      "box-shadow:0 12px 28px rgba(0,0,0,.34)!important;"+
      "backdrop-filter:blur(16px)!important;-webkit-backdrop-filter:blur(16px)!important;}"+
    ".diqV600PlaceBody{padding:18px 18px 24px!important;}"+
    ".diqV601PlaceEyebrowRow{display:flex;align-items:center;justify-content:space-between;"+
      "gap:10px;margin-bottom:8px;}"+
    ".diqV600PlaceEyebrow{color:#8ff6dd!important;font:950 9px/1 system-ui!important;"+
      "letter-spacing:1.45px!important;}"+
    ".diqV601PlacePickBadge{display:inline-flex;align-items:center;justify-content:center;"+
      "min-height:26px;padding:0 10px;border-radius:999px;"+
      "color:#ffe79a;background:rgba(255,216,100,.08);"+
      "border:1px solid rgba(255,216,100,.30);"+
      "font:950 8px/1 system-ui;letter-spacing:1px;white-space:nowrap;}"+
    ".diqV600PlaceTitle{margin:0 0 7px!important;color:#f9fcff!important;"+
      "font:1000 30px/1.04 system-ui!important;letter-spacing:-.85px!important;}"+
    ".diqV600PlaceAddress{margin-bottom:12px!important;color:rgba(226,237,244,.65)!important;"+
      "font:700 12px/1.35 system-ui!important;}"+
    ".diqV600Verdict{margin-top:13px!important;padding:15px 16px!important;border-radius:19px!important;"+
      "background:linear-gradient(135deg,rgba(255,216,100,.10),rgba(73,238,191,.055))!important;"+
      "border:1px solid rgba(255,216,100,.22)!important;"+
      "box-shadow:0 12px 28px rgba(0,0,0,.16)!important;}"+
    ".diqV600VerdictIcon{font-size:20px!important;}"+
    ".diqV600VerdictLabel{color:#ffe492!important;font-size:10px!important;letter-spacing:1.25px!important;}"+
    ".diqV600VerdictSub{color:rgba(231,240,245,.72)!important;font-size:11px!important;}"+
    ".diqV600DecisionBar{margin-top:11px!important;gap:7px!important;}"+
    ".diqV600DecisionPill{padding:8px 10px!important;border-radius:12px!important;"+
      "background:rgba(10,26,39,.74)!important;border-color:rgba(255,255,255,.08)!important;"+
      "font-size:10px!important;}"+
    ".diqV600TonightIQ{margin-top:13px!important;padding:15px!important;border-radius:19px!important;}"+
    ".diqV600SocialProof{margin-top:12px!important;padding:11px 13px!important;border-radius:15px!important;"+
      "background:rgba(255,255,255,.032)!important;border-color:rgba(255,255,255,.065)!important;}"+
    ".diqV600PrimaryActions{margin-top:14px!important;}"+
    ".diqV600RouteHere{margin-top:0!important;min-height:58px!important;padding:0 18px!important;"+
      "border-radius:20px!important;"+
      "background:linear-gradient(135deg,#fff0a8 0%,#ffd76a 38%,#67e7b8 74%,#42e8c8 100%)!important;"+
      "box-shadow:0 18px 38px rgba(0,0,0,.32),0 0 24px rgba(255,216,100,.13)!important;"+
      "color:#071219!important;font:1000 15px/1 system-ui!important;letter-spacing:.9px!important;}"+
    ".diqV600QuickActions{grid-template-columns:repeat(3,1fr)!important;gap:8px!important;margin-top:9px!important;}"+
    ".diqV600QuickAction{min-height:44px!important;padding:0 8px!important;border-radius:15px!important;"+
      "background:linear-gradient(180deg,rgba(12,29,43,.92),rgba(5,18,29,.95))!important;"+
      "border:1px solid rgba(255,255,255,.09)!important;"+
      "box-shadow:0 10px 22px rgba(0,0,0,.20)!important;"+
      "color:#f4fbff!important;font:900 10px/1 system-ui!important;letter-spacing:.55px!important;}"+
    ".diqV600QuickAction[data-place-listen='1']{border-color:rgba(72,241,195,.22)!important;}"+
    ".diqV600WebsiteLink{display:flex;align-items:center;justify-content:center;"+
      "min-height:44px;margin-top:9px;padding:0 14px;border-radius:15px;"+
      "background:rgba(12,29,43,.88);border:1px solid rgba(72,241,195,.23);"+
      "color:#dffbf5;text-decoration:none;font:900 11px/1 system-ui;"+
      "letter-spacing:.6px;}"+
    ".diqV600WebsiteLink:focus-visible{outline:2px solid #7fffd7;outline-offset:3px;}"+
    ".diqV600WhyBlock{margin-top:14px!important;padding:16px!important;border-radius:20px!important;"+
      "background:linear-gradient(145deg,rgba(255,255,255,.038),rgba(72,241,195,.028))!important;"+
      "border:1px solid rgba(72,241,195,.12)!important;}"+
    ".diqV600WhyLabel{color:#8ff6dd!important;font-size:8.5px!important;letter-spacing:1.35px!important;}"+
    ".diqV600PlaceReason{margin-top:8px!important;color:#fff0bd!important;"+
      "font:850 13px/1.45 system-ui!important;}"+
    ".diqV600DecisionChips{gap:7px!important;margin-top:10px!important;}"+
    ".diqV600DecisionChip,.diqV600PlaceChip{padding:8px 10px!important;border-radius:12px!important;"+
      "background:rgba(255,255,255,.035)!important;border-color:rgba(255,255,255,.075)!important;"+
      "font-size:9.5px!important;}"+
    ".diqV600WorthKnowing,.diqV600ReviewHighlights{margin-top:13px!important;padding:15px!important;"+
      "border-radius:19px!important;background:rgba(255,255,255,.028)!important;"+
      "border-color:rgba(255,255,255,.065)!important;}"+
    ".diqV600WorthKnowingLabel,.diqV600ReviewHighlightsLabel{font-size:8.5px!important;"+
      "letter-spacing:1.2px!important;}"+
    "body.diqPlaceIQMode #diqV600ResultsPill,"+
    "body.diq-v600-place #diqV600ResultsPill{"+
      "display:none!important;visibility:hidden!important;opacity:0!important;pointer-events:none!important;"+
    "}"+
    "@media(max-width:420px){"+
      "#diqAdventureInfoCard{left:10px!important;right:10px!important;bottom:calc(10px + env(safe-area-inset-bottom,0px))!important;"+
        "max-height:84dvh!important;border-radius:27px!important;}"+
      ".diqV600PlaceCardInner{border-radius:27px!important;}"+
      ".diqV600Gallery{height:248px!important;border-radius:26px 26px 0 0!important;}"+
      ".diqV600PlaceBody{padding:16px 15px 20px!important;}"+
      ".diqV600PlaceTitle{font-size:27px!important;}"+
      ".diqV600RouteHere{min-height:56px!important;}"+
      ".diqV601PlacePickBadge{font-size:7.5px!important;padding:0 9px!important;}"+
    "}";

  (document.head||document.documentElement).appendChild(style);
  return true;
}


function clearHeroTimers(){
  if(heroHydrationTimer){
    window.clearTimeout(heroHydrationTimer);
    heroHydrationTimer=null;
  }

  if(heroRecoveryTimer){
    window.clearTimeout(heroRecoveryTimer);
    heroRecoveryTimer=null;
  }
}

function fallbackVisual(item){
  var category=clean(
    item&&(
      item.userFacingCategory||
      item.categoryLabel||
      item.category||
      item.poiClass||
      item.entityType||
      ""
    )
  ).toLowerCase();

  if(/coffee|cafe|bakery/.test(category)){
    return {
      image:
        "radial-gradient(circle at 24% 18%,rgba(255,224,151,.6),transparent 36%),"+
        "linear-gradient(145deg,#604123 0%,#2b4d43 56%,#0a202c 100%)",
      mark:"☕"
    };
  }

  if(/food|restaurant|dining|bbq|burger|taco|seafood/.test(category)){
    return {
      image:
        "radial-gradient(circle at 24% 18%,rgba(255,188,85,.58),transparent 35%),"+
        "linear-gradient(145deg,#69351f 0%,#274b3c 58%,#0a202c 100%)",
      mark:"✦"
    };
  }

  if(/dessert|ice cream|gelato|donut|sweet|candy/.test(category)){
    return {
      image:
        "radial-gradient(circle at 24% 18%,rgba(255,196,224,.58),transparent 35%),"+
        "linear-gradient(145deg,#6b3154 0%,#4b385f 52%,#122231 100%)",
      mark:"◇"
    };
  }

  if(/outdoor|park|trail|scenic|lake|water|beach|gulf|ocean|river|bay|marina|harbor|pier|boardwalk/.test(category)){
    return {
      image:
        "radial-gradient(circle at 25% 18%,rgba(104,242,205,.46),transparent 36%),"+
        "linear-gradient(145deg,#174d48 0%,#173b55 58%,#081d2a 100%)",
      mark:"⌁"
    };
  }

  if(/music|nightlife|bar|brewery/.test(category)){
    return {
      image:
        "radial-gradient(circle at 26% 18%,rgba(255,112,208,.44),transparent 35%),"+
        "linear-gradient(145deg,#522d62 0%,#164555 58%,#081d2a 100%)",
      mark:"♪"
    };
  }

  if(/shop|shopping|store|retail|market|boutique|mall/.test(category)){
    return {
      image:
        "radial-gradient(circle at 25% 18%,rgba(126,205,255,.44),transparent 36%),"+
        "linear-gradient(145deg,#253f65 0%,#254b57 56%,#0a1f2d 100%)",
      mark:"▣"
    };
  }

  if(/historic|history|museum|landmark|monument|heritage|district/.test(category)){
    return {
      image:
        "radial-gradient(circle at 24% 18%,rgba(255,216,133,.48),transparent 36%),"+
        "linear-gradient(145deg,#624628 0%,#39483f 56%,#12212d 100%)",
      mark:"◆"
    };
  }

  return {
    image:
      "radial-gradient(circle at 25% 20%,rgba(70,242,195,.4),transparent 36%),"+
      "linear-gradient(145deg,#173348 0%,#164a45 52%,#09202e 100%)",
    mark:"✦"
  };
}

function paintFallback(item,hero){
  var fallback;

  if(!hero){return false;}

  fallback=fallbackVisual(item);
  hero.style.backgroundImage=fallback.image;
  hero.style.backgroundColor="#10202c";
  hero.style.opacity="1";
  hero.setAttribute("data-photo-fallback","1");
  hero.setAttribute("data-fallback-mark",fallback.mark);
  hero.setAttribute("data-photo-state","fallback");

  if(hero.parentNode&&hero.parentNode.classList){
    hero.parentNode.classList.remove("diqGalleryHasPhoto");
  }

  return true;
}

function clearFallbackState(hero){
  if(!hero){return false;}

  hero.removeAttribute("data-photo-fallback");
  hero.removeAttribute("data-fallback-mark");
  hero.setAttribute("data-photo-state","loaded");

  if(hero.parentNode&&hero.parentNode.classList){
    hero.parentNode.classList.add("diqGalleryHasPhoto");
  }

  return true;
}

function backgroundURL(value){
  var match=String(value||"").match(/url\((?:"|')?([^"')]+)(?:"|')?\)/i);
  return match&&match[1]?clean(match[1]):"";
}

function verifyAndCommitPhoto(item,hero,url,options){
  var probe;
  var key=placeKey(item);
  var settings=options||{};

  if(!hero||!url){return false;}

  hero.setAttribute("data-photo-state","checking");

  probe=new window.Image();

  probe.onload=function(){
    var currentCard=getCard();
    var currentHero;

    if(
      !activeItem||
      placeKey(activeItem)!==key||
      !currentCard
    ){
      return;
    }

    currentHero=currentCard.querySelector("[data-diq-photo-hero]");
    if(!currentHero||currentHero!==hero){return;}

    if(
      Number(probe.naturalWidth||0)<24||
      Number(probe.naturalHeight||0)<24
    ){
      paintFallback(item,currentHero);
      return;
    }

    if(settings.commit!==false){
      currentHero.style.backgroundImage=
        'url("'+String(url).replace(/"/g,'\\"')+'")';
    }

    currentHero.style.backgroundColor="#10202c";
    currentHero.style.opacity="1";
    clearFallbackState(currentHero);
  };

  probe.onerror=function(){
    var currentCard=getCard();
    var currentHero;

    if(
      !activeItem||
      placeKey(activeItem)!==key||
      !currentCard
    ){
      return;
    }

    currentHero=currentCard.querySelector("[data-diq-photo-hero]");
    if(currentHero&&currentHero===hero){
      paintFallback(item,currentHero);
    }
  };

  try{
    probe.decoding="async";
  }catch(error){}

  probe.src=url;

  return true;
}

function monitorHydratedHero(item,attempt){
  var currentCard=getCard();
  var currentHero;
  var imageValue;
  var url;
  var tries=Number(attempt)||0;

  if(
    !currentCard||
    !activeItem||
    placeKey(activeItem)!==placeKey(item)
  ){
    return;
  }

  currentHero=currentCard.querySelector("[data-diq-photo-hero]");
  if(!currentHero){return;}

  imageValue=String(currentHero.style.backgroundImage||"");
  url=backgroundURL(imageValue);

  if(url){
    verifyAndCommitPhoto(item,currentHero,url,{commit:false});
    return;
  }

  if(tries>=8){
    paintFallback(item,currentHero);
    return;
  }

  heroRecoveryTimer=window.setTimeout(function(){
    heroRecoveryTimer=null;
    monitorHydratedHero(item,tries+1);
  },450);
}

function hydrateHeroSafely(item){
  var c=getCard();
  var hero;
  var direct;

  clearHeroTimers();

  if(!c||!item){return false;}

  hero=c.querySelector("[data-diq-photo-hero]");
  if(!hero){return false;}

  /*
    V600.40 Media Authority:
    The fallback is the first committed visual. Remote media must prove that it
    loaded successfully before it is allowed to replace the fallback.
  */
  paintFallback(item,hero);

  direct=directPhoto(item);
  if(direct){
    verifyAndCommitPhoto(item,hero,direct,{commit:true});
  }

  heroHydrationTimer=window.setTimeout(function(){
    var currentCard=getCard();
    var currentHero;

    heroHydrationTimer=null;

    if(
      !currentCard||
      !activeItem||
      placeKey(activeItem)!==placeKey(item)
    ){
      return;
    }

    currentHero=currentCard.querySelector("[data-diq-photo-hero]");
    if(!currentHero){return;}

    try{
      if(
        window.DriveIQAdventurePhoto&&
        typeof window.DriveIQAdventurePhoto.hydrateElement==="function"
      ){
        window.DriveIQAdventurePhoto.hydrateElement(
          item,
          currentHero,
          {
            source:"place-card-hero",
            role:"hero"
          }
        );
      }
    }catch(error){
      paintFallback(item,currentHero);
    }

    monitorHydratedHero(item,0);
  },80);

  return true;
}

function directPhoto(item){
  return clean(
    item.diqPhotoUrl||
    item.photoURL||
    item.photoUrl||
    item.heroPhoto||
    item.heroImage||
    item.image||
    ""
  );
}

function photoGallery(item){
  var out=[];
  var bundle=null;
  var list=[];
  var i;
  var url;

  try{
    if(
      window.DriveIQAdventurePhoto&&
      typeof window.DriveIQAdventurePhoto.photoBundle==="function"
    ){
      bundle=window.DriveIQAdventurePhoto.photoBundle(item);
    }
  }catch(error){}

  if(bundle&&Array.isArray(bundle.gallery)){
    list=list.concat(bundle.gallery);
  }

  if(Array.isArray(item.gallery)){list=list.concat(item.gallery);}
  if(Array.isArray(item.photos)){list=list.concat(item.photos);}

  list.unshift(directPhoto(item));

  for(i=0;i<list.length;i+=1){
    url=clean(
      typeof list[i]==="string"
        ? list[i]
        : list[i]&&list[i].url
    );

    if(url&&out.indexOf(url)<0){
      out.push(url);
    }
  }

  return out.slice(0,5);
}

function categoryLabel(item){
  return clean(
    item.userFacingCategory||
    item.categoryLabel||
    item.category||
    item.poiClass||
    item.entityType||
    "PLACE IQ"
  );
}


function itemText(item){
  return clean([
    item&&item.label,
    item&&item.name,
    item&&item.category,
    item&&item.categoryLabel,
    item&&item.primaryCategory,
    item&&item.userFacingCategory,
    item&&item.address,
    item&&item.full,
    item&&item.description,
    item&&item.summary,
    item&&item.eventTitle,
    item&&item.eventType,
    item&&item.eventRecurrenceText,
    item&&item.eventFloorOrArea,
    item&&item.eventConfidence,
    item&&item.recommendedBecause,
    item&&item.reason,
    item&&item.why,
    item&&item.tags&&item.tags.join
      ? item.tags.join(" ")
      : "",
    item&&item.types&&item.types.join
      ? item.types.join(" ")
      : ""
  ].join(" ")).toLowerCase();
}

function categoryKey(item){
  var value=categoryLabel(item).toLowerCase();

  if(/coffee|cafe|café|espresso|roaster/.test(value)){return "coffee";}
  if(/restaurant|food|dining|bbq|burger|pizza|taco|seafood/.test(value)){return "food";}
  if(/music|concert|honky tonk|jazz|blues/.test(value)){return "live_music";}
  if(/nightlife|bar|pub|lounge|club/.test(value)){return "nightlife";}
  if(/waterfront|water|beach|bay|gulf|ocean|lake|river|marina|harbor|harbour|pier|boardwalk|boat ramp/.test(value)){return "waterfront";}
  if(/park|trail|outdoor|scenic|nature/.test(value)){return "outdoors";}
  if(/dessert|ice cream|bakery/.test(value)){return "dessert";}

  return "place";
}

function distanceMiles(item){
  var direct=numberValue(
    item.distanceMiles||
    item.distance_miles
  );

  if(direct!==null){
    return Math.max(0,direct);
  }

  var meters=numberValue(
    item.distanceMeters||
    item.distance_meters
  );

  if(meters!==null){
    return Math.max(0,meters/1609.344);
  }

  return null;
}


function stableNumber(value){
  var text=String(value||"");
  var hash=0;
  var i;

  for(i=0;i<text.length;i+=1){
    hash=((hash<<5)-hash)+text.charCodeAt(i);
    hash|=0;
  }

  return Math.abs(hash);
}

function stableChoice(item,choices,salt){
  var list=Array.isArray(choices)?choices:[];
  var seed;

  if(!list.length){return "";}

  seed=stableNumber(
    placeKey(item)+"|"+String(salt||"companion")
  );

  return list[seed%list.length];
}

function placeName(item){
  return clean(
    item&&(
      item.label||
      item.name
    )||
    "this place"
  );
}

function isChainPlace(item){
  return !!(
    item&&item.isChain===true||
    /starbucks|dunkin|panera|mcdonald|chick-fil-a|chick fil a|burger king|wendy|taco bell|subway|applebee|chili|olive garden/.test(
      itemText(item)
    )
  );
}

function isIconicPlace(item){
  var text=itemText(item);

  return /iconic|landmark|historic|famous|well[- ]known|signature|must[- ]see|must visit|broadway|beale street|french quarter|downtown/.test(text);
}

function isLocalPlace(item){
  var text=itemText(item);

  return !!(
    item&&item.isLocal===true||
    item&&item.isChain===false||
    /local favorite|family owned|family-owned|independent|neighborhood|locally owned/.test(text)
  );
}

function hasEasyAccess(item){
  return /parking|easy access|drive[- ]?thru|drive through|quick pickup|mobile pickup|pickup/.test(
    itemText(item)
  );
}

function companionHeadline(item){
  var category=categoryKey(item);
  var text=itemText(item);
  var distance=distanceMiles(item);
  var close=distance!==null&&distance<=2;
  var nearby=distance!==null&&distance<=4;
  var chain=isChainPlace(item);
  var local=isLocalPlace(item);
  var iconic=isIconicPlace(item);
  var easy=hasEasyAccess(item);

  if(category==="coffee"){
    if(/roaster|roastery|specialty coffee|single origin/.test(text)){
      return stableChoice(item,[
        "A more memorable coffee stop when you want something beyond the usual",
        "A stronger coffee pick for flavor, atmosphere, and a little more character",
        "A coffee stop worth choosing when the experience matters too"
      ],"coffee-specialty");
    }

    if(local&&!chain){
      return stableChoice(item,[
        "A strong local coffee pick that feels worth the stop",
        "One of the better nearby coffee choices when you want something local",
        "A local coffee stop with more personality than the usual chain"
      ],"coffee-local");
    }

    if(close||nearby){
      return stableChoice(item,[
        easy
          ? "A reliable coffee stop that is close by and easy to get in and out of"
          : "A reliable coffee stop nearby without taking you far out of the way",
        "A practical nearby coffee pick when you want to get moving again quickly",
        "A solid coffee choice close by with very little added drive time"
      ],"coffee-nearby");
    }

    return stableChoice(item,[
      "A dependable coffee stop that still makes sense for this trip",
      "A solid coffee option when you want reliability more than a long detour",
      "A coffee stop worth considering when convenience matters"
    ],"coffee-default");
  }

  if(category==="food"){
    if(iconic){
      return stableChoice(item,[
        "A well-known local stop that gives you more of the destination experience",
        "An iconic food pick that feels like part of exploring the area",
        "A recognizable local destination with more character than a routine meal stop"
      ],"food-iconic");
    }

    if(local&&!chain){
      return stableChoice(item,[
        "A strong local food pick that feels more memorable than the usual options",
        "One of the better nearby choices when you want something local",
        "A local restaurant that looks worth choosing over the familiar chains"
      ],"food-local");
    }

    if(close||nearby){
      return stableChoice(item,[
        "A strong nearby food pick without adding much time to your drive",
        "A practical meal stop close by that still feels like a good choice",
        "A solid place to eat nearby without turning it into a long detour"
      ],"food-nearby");
    }

    return stableChoice(item,[
      "A food stop that looks worth the extra drive",
      "A stronger meal choice when you are willing to go a little farther",
      "A place that earns consideration beyond simple convenience"
    ],"food-default");
  }

  if(category==="live_music"){
    return stableChoice(item,[
      iconic
        ? "A signature local music stop with the kind of atmosphere people remember"
        : "A stronger live music pick when the atmosphere matters as much as the lineup",
      "A good place to catch live entertainment and feel more of the local scene",
      "A live music destination worth choosing for the experience, not just the distance"
    ],"live-music");
  }

  if(category==="nightlife"){
    return stableChoice(item,[
      iconic
        ? "A recognizable nightlife stop that helps you experience the district"
        : "A stronger evening pick when you want energy and atmosphere",
      "A good fit for the night when the vibe matters more than simply finding the nearest bar",
      "A nightlife choice that looks worth the stop for the atmosphere alone"
    ],"nightlife");
  }

  if(category==="waterfront"){
    if(/navarre beach/.test(text)){
      return stableChoice(item,[
        "A quieter Gulf Coast beach with sugar-white sand, clear water, and room to slow down",
        "Worth the drive when you want a beautiful Gulf beach with a calmer feel than the busiest Destin spots",
        "A scenic Gulf Coast favorite for swimming, long beach walks, sunsets, and a less crowded day by the water"
      ],"waterfront-navarre");
    }

    if(/beach|gulf|ocean|shore|coast/.test(text)){
      if(/quiet|secluded|uncrowded|less crowded/.test(text)){
        return stableChoice(item,[
          "A calmer beach choice when you want more space, better views, and less of the crowd",
          "A quieter waterfront escape that feels worth the extra drive",
          "A scenic beach stop for slowing down without the busiest tourist atmosphere"
        ],"waterfront-quiet-beach");
      }

      return stableChoice(item,[
        "A strong beach destination for water views, swimming, sunsets, and a real Gulf Coast experience",
        "A scenic waterfront stop that feels worth choosing for the beach and the setting",
        "A beach destination that gives you more than a quick stop by the water"
      ],"waterfront-beach");
    }

    if(/marina|harbor|harbour|boat ramp|boating/.test(text)){
      return stableChoice(item,[
        "A useful waterfront stop for boating access, marina views, and time near the water",
        "A strong marina-area pick when the water and easy access matter",
        "A waterfront destination that works well for boating, views, and a relaxed stop"
      ],"waterfront-marina");
    }

    if(/pier|boardwalk/.test(text)){
      return stableChoice(item,[
        "A walkable waterfront stop with views, atmosphere, and an easy way to enjoy the shoreline",
        "A strong boardwalk or pier pick when you want scenery without committing to a full beach day",
        "A waterfront experience worth choosing for the views, walking, and local atmosphere"
      ],"waterfront-boardwalk");
    }

    if(/lake|river|bay/.test(text)){
      return stableChoice(item,[
        "A scenic water destination that gives you room to slow down and enjoy the setting",
        "A stronger waterside choice when the view and atmosphere matter",
        "A relaxing stop by the water that feels worth more than a quick glance"
      ],"waterfront-inland");
    }

    return stableChoice(item,[
      "A waterfront destination worth choosing for the setting, the views, and the experience",
      "A stronger water-focused pick when you want more than the closest shoreline",
      "A scenic place near the water that feels worth adding to the trip"
    ],"waterfront-default");
  }

  if(category==="outdoors"){
    if(/waterfront|riverfront|lake|beach|water view/.test(text)){
      return stableChoice(item,[
        "A relaxing outdoor stop with water nearby and a real sense of place",
        "A stronger scenic choice when you want water, views, and room to slow down",
        "An outdoor stop worth choosing for the setting as much as the destination"
      ],"outdoors-water");
    }

    return stableChoice(item,[
      "A good reason to step away from the road and explore for a while",
      "One of the better nearby outdoor options when you want a change of scenery",
      "An outdoor stop that looks worth the time for the experience and setting"
    ],"outdoors");
  }

  if(category==="dessert"){
    return stableChoice(item,[
      close
        ? "A fun dessert stop close by that is easy to work into the trip"
        : "A sweet stop that feels worth adding to the drive",
      "A good nearby choice when the trip could use something fun",
      "A dessert stop that makes more sense than settling for the first option"
    ],"dessert");
  }

  return stableChoice(item,[
    "A place Drive IQ thinks is worth more than a quick glance",
    "A stronger nearby option based on the experience and current context",
    "A place that stands out enough to deserve your attention"
  ],"place");
}

function decisionHeadline(item){
  return companionHeadline(item);
}

function decisionChips(item){
  var text=itemText(item);
  var category=categoryKey(item);
  var chips=[];
  var distance=distanceMiles(item);
  var rating=numberValue(
    item.rating||
    item.googleRating||
    item.averageRating
  );
  var reviews=reviewCountValue(item);

  function add(label){
    if(label&&chips.indexOf(label)<0&&chips.length<4){
      chips.push(label);
    }
  }

  if(distance!==null){
    if(distance<=1.5){add("Very Close");}
    else if(distance<=4){add("Nearby");}
    else if(distance>=10){add("Worth the Drive");}
  }

  if(item.openNow===true||item.isOpen===true||item.open_now===true){
    add("Open Now");
  }

  if(rating!==null&&rating>=4.6){add("Highly Rated");}
  else if(rating!==null&&rating>=4.3){add("Strong Reviews");}

  if(reviews!==null&&reviews>=500){add("Well Established");}

  if(/parking|easy access|drive[- ]?thru|drive through/.test(text)){
    add("Easy Parking");
  }

  if(/mobile order|mobile pickup|pickup|drive[- ]?thru|drive through/.test(text)){
    add("Quick Pickup");
  }

  if(/local favorite|family owned|family-owned|independent|neighborhood/.test(text)){
    add("Local Favorite");
  }

  if(/workspace|work from|wifi|wi-fi|study|laptop/.test(text)){
    add("Great Workspace");
  }

  if(/patio|outdoor seating/.test(text)){add("Patio");}
  if(/rooftop/.test(text)){add("Rooftop");}
  if(/waterfront|riverfront|water view|beach|gulf|ocean|bay|lake|river/.test(text)){add("Waterfront");}
  if(/quiet|quieter|secluded|uncrowded|less crowded/.test(text)){add("Quieter Escape");}
  if(/family|kid friendly|kids|playground/.test(text)){add("Family Friendly");}
  if(/sunset|sunrise/.test(text)){add("Sunset Spot");}
  if(/swim|swimming|beach access/.test(text)){add("Great for Swimming");}
  if(/marina|harbor|harbour|boat ramp|boating/.test(text)){add("Boating Access");}
  if(/pier|boardwalk/.test(text)){add("Walkable Waterfront");}
  if(/fishing|fish pier/.test(text)){add("Fishing Spot");}
  if(/breakfast|brunch|pastry|bakery/.test(text)){add("Food Too");}
  if(/live music|music venue|concert/.test(text)){add("Live Music");}

  if(category==="coffee"&&chips.length<3){
    add("Coffee Match");
  }

  return chips;
}

function ratingValue(item){
  return numberValue(
    item&&(
      item.rating||
      item.googleRating||
      item.averageRating
    )
  );
}

function reviewCountValue(item){
  var candidates=[
    item&&item.userRatingCount,
    item&&item.userRatingsTotal,
    item&&item.reviewCount,
    item&&item.ratingCount,
    item&&item.reviewsCount
  ];
  var i;
  var value;

  for(i=0;i<candidates.length;i+=1){
    value=numberValue(candidates[i]);

    if(value!==null&&value>=0){
      return Math.round(value);
    }
  }

  return null;
}

function compactCount(value){
  var count=numberValue(value);

  if(count===null||count<0){return "";}

  if(count>=1000000){
    return (Math.round(count/100000)/10).toFixed(
      count>=10000000?0:1
    ).replace(/\.0$/,"")+"M";
  }

  if(count>=1000){
    return (Math.round(count/100)/10).toFixed(
      count>=100000?0:1
    ).replace(/\.0$/,"")+"K";
  }

  return String(Math.round(count));
}

function socialProofText(item){
  var rating=ratingValue(item);
  var count=reviewCountValue(item);

  if(rating===null){return "";}

  if(count!==null&&count>0){
    return "★ "+rating.toFixed(1)+" · "+compactCount(count)+" Google ratings";
  }

  return "★ "+rating.toFixed(1)+" Google rating";
}

function driveIQVerdict(item){
  var category=categoryKey(item);
  var text=itemText(item);
  var distance=distanceMiles(item);
  var rating=ratingValue(item);
  var count=reviewCountValue(item);
  var label="DRIVE IQ PICK";
  var icon="✦";
  var sub="A strong choice for this moment";
  var event=eventRecord(item);
  var eventState=eventConfidence(item);

  if(eventState==="confirmed_tonight"){
    icon="🎸";
    label="LIVE TONIGHT";
    sub=eventTimeText(item)
      ? eventTypeLabel(item)+" starts "+eventTimeText(item)
      : "Confirmed live experience tonight";
  }else if(eventState==="upcoming_event"){
    icon="🎟";
    label="UPCOMING EVENT";
    sub=eventTimeText(item)
      ? eventTypeLabel(item)+" at "+eventTimeText(item)
      : "A scheduled experience worth planning around";
  }else if(eventState==="usually_happens_tonight"){
    icon="♪";
    label="USUALLY HAPPENS TONIGHT";
    sub=event&&event.recurrenceText
      ? event.recurrenceText
      : "A recurring venue experience";
  }else if(eventState==="known_music_venue"){
    icon="🎶";
    label="KNOWN MUSIC VENUE";
    sub="Frequently hosts live entertainment";
  }else if(category==="waterfront"){
    icon="🏖";
    label=/sunset|sunrise/.test(text)
      ? "SUNSET PICK"
      : "DRIVE IQ WATERFRONT PICK";
    sub=/quiet|quieter|uncrowded|less crowded|navarre beach/.test(text)
      ? "Better when you want a calmer day by the water"
      : "Chosen for the setting, views, and experience";
  }else if(category==="food"){
    icon="🍽";
    label=isLocalPlace(item)
      ? "LOCAL FOOD PICK"
      : "DRIVE IQ FOOD PICK";
    sub=isLocalPlace(item)
      ? "A stronger local choice over the usual options"
      : "A meal stop that fits the trip";
  }else if(category==="coffee"){
    icon="☕";
    label="COFFEE PICK";
    sub="A coffee stop that makes sense for this drive";
  }else if(category==="live_music"){
    icon="♪";
    label="LIVE MUSIC PICK";
    sub="Chosen for atmosphere and local energy";
  }else if(category==="nightlife"){
    icon="🌙";
    label="EVENING PICK";
    sub="A stronger choice when the vibe matters";
  }else if(category==="outdoors"){
    icon="🌿";
    label="OUTDOOR PICK";
    sub="Worth stepping away from the road for";
  }else if(category==="dessert"){
    icon="🍦";
    label="SWEET STOP";
    sub="A fun add-on that fits the trip";
  }

  if(!eventState&&distance!==null&&distance>=10){
    label="DRIVE IQ RECOMMENDS";
    sub="Worth the extra drive today";
  }

  if(
    !eventState&&
    rating!==null&&
    rating>=4.6&&
    count!==null&&
    count>=500
  ){
    sub="Drive IQ recommends it—and plenty of travelers agree";
  }

  return {
    icon:icon,
    label:label,
    sub:sub
  };
}

function verdictMarkup(item){
  var verdict=driveIQVerdict(item);

  return (
    '<div class="diqV600Verdict">'+
      '<span class="diqV600VerdictIcon">'+escape(verdict.icon)+'</span>'+
      '<span class="diqV600VerdictCopy">'+
        '<span class="diqV600VerdictLabel">'+escape(verdict.label)+'</span>'+
        '<span class="diqV600VerdictSub">'+escape(verdict.sub)+'</span>'+
      '</span>'+
    '</div>'
  );
}

function socialProofMarkup(item){
  var proof=socialProofText(item);

  if(!proof){return "";}

  return (
    '<div class="diqV600SocialProof">'+
      '<strong>'+escape(proof)+'</strong>'+
      '<span class="diqV600SocialProofSource">Google</span>'+
    '</div>'
  );
}

function eventRecord(item){
  if(!item){return null;}

  if(item.primaryEvent&&typeof item.primaryEvent==="object"){
    return item.primaryEvent;
  }

  if(
    item.eventTitle||
    item.eventStartTime||
    item.eventRecurrenceText||
    item.eventConfidence
  ){
    return {
      eventTitle:item.eventTitle||"",
      eventType:item.eventType||"",
      startTime:item.eventStartTime||"",
      endTime:item.eventEndTime||"",
      floorOrArea:item.eventFloorOrArea||"",
      sourceType:item.eventSourceType||"",
      sourceURL:item.eventSourceURL||"",
      confirmedAt:item.eventConfirmedAt||"",
      recurring:item.eventRecurring===true,
      recurrenceText:item.eventRecurrenceText||"",
      confidence:item.eventConfidence||""
    };
  }

  return null;
}

function eventDate(value){
  var date;

  if(!value){return null;}

  date=new Date(value);
  return isNaN(date.getTime())?null:date;
}

function eventConfidence(item){
  var event=eventRecord(item);

  return clean(
    event&&event.confidence||
    item&&item.eventConfidence||
    ""
  ).toLowerCase().replace(/\s+/g,"_");
}

function eventTypeLabel(item){
  var event=eventRecord(item);
  var type=clean(
    event&&event.eventType||
    item&&item.eventType||
    ""
  ).toLowerCase().replace(/\s+/g,"_");

  if(type==="live_music"){return "LIVE MUSIC";}
  if(type==="karaoke"){return "KARAOKE";}
  if(type==="trivia"){return "TRIVIA NIGHT";}
  if(type==="open_mic"){return "OPEN MIC";}
  if(type==="dj"){return "DJ";}
  if(type==="watch_party"){return "WATCH PARTY";}
  if(type==="happy_hour"){return "HAPPY HOUR";}
  if(type==="festival"){return "FESTIVAL";}
  if(type==="comedy"){return "COMEDY";}
  if(type==="show"){return "SHOW";}

  return "EVENT";
}

function eventConfidenceLabel(item){
  var confidence=eventConfidence(item);

  if(confidence==="confirmed_tonight"){return "CONFIRMED TONIGHT";}
  if(confidence==="upcoming_event"){return "UPCOMING EVENT";}
  if(confidence==="usually_happens_tonight"){return "USUALLY HAPPENS TONIGHT";}
  if(confidence==="known_music_venue"){return "KNOWN MUSIC VENUE";}

  return "";
}

function eventTimeText(item){
  var event=eventRecord(item);
  var start=eventDate(event&&event.startTime);
  var recurrence=clean(
    event&&event.recurrenceText||
    item&&item.eventRecurrenceText||
    ""
  );

  if(start){
    try{
      return start.toLocaleTimeString([],{
        hour:"numeric",
        minute:start.getMinutes()?"2-digit":undefined
      });
    }catch(error){
      return start.toLocaleTimeString();
    }
  }

  return recurrence;
}

function eventSourceText(item){
  var event=eventRecord(item);
  var source=clean(event&&event.sourceType||"");
  var confirmed=eventDate(event&&event.confirmedAt);
  var sourceLabel="";

  if(/official_website|official website|venue website/i.test(source)){
    sourceLabel="Confirmed from the venue website";
  }else if(/official_social|official social|facebook|instagram/i.test(source)){
    sourceLabel="Confirmed from the venue's official social page";
  }else if(/ticketmaster|bandsintown|eventbrite|event_platform/i.test(source)){
    sourceLabel="Listed by an event platform";
  }else if(/recurring|pattern/i.test(source)){
    sourceLabel="Based on the venue's recurring schedule";
  }else if(source){
    sourceLabel="Event source: "+source.replace(/_/g," ");
  }

  if(confirmed){
    try{
      sourceLabel+=
        (sourceLabel?" · ":"")+
        "verified "+
        confirmed.toLocaleDateString([],{
          month:"short",
          day:"numeric"
        });
    }catch(error2){}
  }

  return clean(sourceLabel);
}

function tonightIQMarkup(item){
  var event=eventRecord(item);
  var confidence=eventConfidenceLabel(item);
  var title;
  var type;
  var time;
  var area;
  var recurrence;
  var source;
  var confirmedClass;

  if(!event&&!confidence){return "";}

  title=clean(
    event&&event.eventTitle||
    eventTypeLabel(item)
  );
  type=eventTypeLabel(item);
  time=eventTimeText(item);
  area=clean(
    event&&event.floorOrArea||
    item&&item.eventFloorOrArea||
    ""
  );
  recurrence=clean(
    event&&event.recurrenceText||
    item&&item.eventRecurrenceText||
    ""
  );
  source=eventSourceText(item);
  confirmedClass=eventConfidence(item)==="confirmed_tonight"
    ?" confirmed"
    :"";

  if(!title&&confidence==="KNOWN MUSIC VENUE"){
    title="Frequently hosts live entertainment";
  }

  return (
    '<div class="diqV600TonightIQ'+confirmedClass+'">'+
      '<div class="diqV600TonightIQHeader">'+
        '<span class="diqV600TonightIQBadge">🎸 '+escape(confidence||"TONIGHT IQ")+'</span>'+
        '<span class="diqV600TonightIQType">'+escape(type)+'</span>'+
      '</div>'+
      '<div class="diqV600TonightIQTitle">'+escape(title||type)+'</div>'+
      (
        time||area||recurrence
          ? '<div class="diqV600TonightIQDetails">'+
              (time?'<span class="diqV600TonightIQDetail">🕒 '+escape(time)+'</span>':"")+
              (area?'<span class="diqV600TonightIQDetail">📍 '+escape(area)+'</span>':"")+
              (!time&&recurrence
                ? '<span class="diqV600TonightIQDetail">'+escape(recurrence)+'</span>'
                : "")+
            '</div>'
          : ""
      )+
      (source?'<div class="diqV600TonightIQSource">'+escape(source)+'</div>':"")+
    '</div>'
  );
}

function worthKnowingItems(item){
  var text=itemText(item);
  var category=categoryKey(item);
  var items=[];

  function add(value){
    if(value&&items.indexOf(value)<0&&items.length<4){
      items.push(value);
    }
  }

  if(category==="waterfront"){
    if(/navarre beach/.test(text)){
      add("Usually calmer than the busiest Destin beaches");
      add("Excellent for swimming, beach walks, and sunsets");
      add("A stronger choice when you want more room to slow down");
    }else{
      if(/quiet|quieter|uncrowded|less crowded/.test(text)){
        add("A calmer waterfront option");
      }
      if(/sunset|sunrise/.test(text)){add("Strong sunset or sunrise potential");}
      if(/swim|swimming|beach access/.test(text)){add("Good fit for swimming and beach time");}
      if(/marina|harbor|boat ramp|boating/.test(text)){add("Useful boating or marina access");}
      if(/pier|boardwalk/.test(text)){add("Easy waterfront walking and views");}
    }
  }

  if(category==="food"){
    if(isLocalPlace(item)){add("A more local choice than the familiar chains");}
    if(/patio|outdoor seating/.test(text)){add("Outdoor seating available");}
    if(/reservation|reservations/.test(text)){add("Reservations may be worth considering");}
    if(/date night|romantic/.test(text)){add("Strong date-night fit");}
  }

  if(category==="coffee"){
    if(isLocalPlace(item)){add("Local coffee character");}
    if(/wifi|wi-fi|workspace|laptop/.test(text)){add("Good fit for working or planning");}
    if(/pastry|bakery|breakfast/.test(text)){add("Food options beyond coffee");}
  }

  if(category==="live_music"){
    add("Best when atmosphere matters as much as the lineup");
    if(/outdoor|patio/.test(text)){add("Outdoor music atmosphere");}
  }

  if(category==="nightlife"){
    add("Better suited for an evening stop");
    if(/rooftop/.test(text)){add("Rooftop atmosphere");}
  }

  if(category==="outdoors"){
    add("Worth allowing extra time to explore");
    if(/trail|hike/.test(text)){add("Plan for walking or trail time");}
  }

  if(/parking fills|parking limited|limited parking/.test(text)){
    add("Parking can fill during busy periods");
  }else if(/parking|easy access/.test(text)){
    add("Relatively easy access or parking");
  }

  if(item.openNow===false||item.isOpen===false||item.open_now===false){
    add("Currently closed—save it for later");
  }

  return items;
}

function worthKnowingMarkup(item){
  var items=worthKnowingItems(item);

  if(!items.length){return "";}

  return (
    '<div class="diqV600WorthKnowing">'+
      '<div class="diqV600WorthKnowingLabel">WORTH KNOWING</div>'+
      '<div class="diqV600WorthKnowingList">'+
        items.map(function(value){
          return (
            '<div class="diqV600WorthKnowingItem">'+
              '<span class="diqV600WorthKnowingDot"></span>'+
              '<span>'+escape(value)+'</span>'+
            '</div>'
          );
        }).join("")+
      '</div>'+
    '</div>'
  );
}

function reviewHighlightItems(item){
  var source=
    item&&(
      item.reviewHighlights||
      item.reviews||
      item.googleReviews
    );
  var out=[];

  if(!Array.isArray(source)){return out;}

  source.forEach(function(review){
    var textValue;
    var author;
    var rating;

    if(out.length>=2){return;}

    if(typeof review==="string"){
      textValue=clean(review);
      author="";
      rating=null;
    }else{
      textValue=clean(
        review&&(
          review.text||
          review.reviewText||
          review.content||
          review.summary||
          ""
        )
      );
      author=clean(
        review&&(
          review.authorName||
          review.author||
          review.reviewerName||
          ""
        )
      );
      rating=numberValue(
        review&&(
          review.rating||
          review.stars
        )
      );
    }

    if(!textValue){return;}

    if(textValue.length>150){
      textValue=textValue.slice(0,147).replace(/\s+\S*$/,"")+"…";
    }

    out.push({
      text:textValue,
      author:author,
      rating:rating
    });
  });

  return out;
}

function reviewHighlightsMarkup(item){
  var reviews=reviewHighlightItems(item);

  if(!reviews.length){return "";}

  return (
    '<div class="diqV600ReviewHighlights">'+
      '<div class="diqV600ReviewHighlightsLabel">TRAVELER HIGHLIGHTS</div>'+
      '<div class="diqV600ReviewList">'+
        reviews.map(function(review){
          var stars="";

          if(review.rating!==null){
            stars="★★★★★".slice(0,Math.max(1,Math.min(5,Math.round(review.rating))));
          }

          return (
            '<div class="diqV600ReviewItem">'+
              '<div class="diqV600ReviewQuote">“'+escape(review.text)+'”</div>'+
              '<div class="diqV600ReviewMeta">'+
                '<span>'+(review.author?escape(review.author):"Google reviewer")+'</span>'+
                '<span class="diqV600ReviewStars">'+escape(stars)+'</span>'+
              '</div>'+
            '</div>'
          );
        }).join("")+
      '</div>'+
    '</div>'
  );
}

function confidenceText(item){
  var truth=item&&item.__categoryTruth;
  var confidence=numberValue(
    truth&&truth.confidence
  );

  if(confidence!==null&&confidence>=0.94){
    return "Top Recommendation";
  }

  return "";
}

function decisionBarMarkup(item){
  var parts=[];
  var rating=ratingText(item);
  var open=openText(item);
  var distance=distanceText(item);
  var confidence=confidenceText(item);

  if(distance){
    parts.push(
      '<span class="diqV600DecisionPill strong">📍 '+
      escape(distance)+
      '</span>'
    );
  }

  if(open){
    parts.push(
      '<span class="diqV600DecisionPill '+(/open/i.test(open)?"good":"warn")+'">'+
      (/open/i.test(open)?"● ":"")+
      escape(open)+
      '</span>'
    );
  }

  if(confidence){
    parts.push(
      '<span class="diqV600DecisionPill">'+
      escape(confidence)+
      '</span>'
    );
  }

  return parts.join("");
}

function decisionChipsMarkup(item){
  return decisionChips(item).map(function(label){
    return '<span class="diqV600DecisionChip">'+
      escape(label)+
      '</span>';
  }).join("");
}

function reasonText(item){
  var supplied=clean(
    item.recommendedBecause||
    item.reason||
    item.why||
    item.summary||
    ""
  );
  var generic=(
    !supplied||
    /^strong .* match$/i.test(supplied)||
    /^worth exploring/i.test(supplied)||
    /^very close$/i.test(supplied)||
    /^nearby$/i.test(supplied)||
    /^top match$/i.test(supplied)||
    /^good match$/i.test(supplied)||
    /^worth considering$/i.test(supplied)||
    /^a place drive iq thinks is worth more than a quick glance$/i.test(supplied)||
    /^a stronger nearby option based on the experience and current context$/i.test(supplied)||
    /^a place that stands out enough to deserve your attention$/i.test(supplied)||
    /^coffee match$/i.test(supplied)||
    /^food match$/i.test(supplied)
  );

  if(!generic){
    return supplied;
  }

  return decisionHeadline(item);
}

function ratingText(item){
  var rating=ratingValue(item);

  if(rating===null){return "";}

  return "★ "+rating.toFixed(1);
}

function reviewsText(item){
  var count=reviewCountValue(item);

  if(count===null||count<1){return "";}

  return compactCount(count)+" Google ratings";
}

function openText(item){
  var open=
    item.openNow||
    item.isOpen||
    item.open_now;

  if(open===true){return "Open now";}
  if(open===false){return "Closed";}

  return clean(item.openStatus||item.hoursStatus||"");
}

function distanceText(item){
  var value=clean(
    item.distanceText||
    item.distanceLabel||
    item.detourText||
    ""
  );

  if(value){return value;}

  if(isFinite(Number(item.distanceMiles))){
    return Number(item.distanceMiles).toFixed(1)+" mi away";
  }

  return "";
}

function buildChips(item){
  var chips=[];
  var open=openText(item);
  var distance=distanceText(item);

  if(open){
    chips.push(
      '<span class="diqV600PlaceChip '+(/open/i.test(open)?"good":"warn")+'">'+
      escape(open)+
      '</span>'
    );
  }

  if(distance){
    chips.push(
      '<span class="diqV600PlaceChip">'+
      escape(distance)+
      '</span>'
    );
  }

  return chips.join("");
}


function spokenNumber(value){
  var number=Number(value);
  var words=[
    "zero","one","two","three","four","five","six","seven","eight","nine",
    "ten","eleven","twelve","thirteen","fourteen","fifteen","sixteen",
    "seventeen","eighteen","nineteen","twenty"
  ];

  if(!isFinite(number)){return "";}

  if(
    Math.round(number)===number&&
    number>=0&&
    number<words.length
  ){
    return words[number];
  }

  return String(number);
}

function applyPronunciationOverrides(value){
  var spoken=clean(value);
  var overrides=[
    [/\bDestin\b/gi,"Dess-tin"],
    [/\bBiloxi\b/gi,"Buh-luck-see"],
    [/\bHernando\b/gi,"Her-nan-doe"],
    [/\bNesbit\b/gi,"Nez-bit"],
    [/\bTchoupitoulas\b/gi,"Chop-ih-too-lus"],
    [/\bNavarre\b/gi,"Nuh-var"],
    [/\bBeale\b/gi,"Beel"],
    [/\bDeSoto\b/gi,"Deh-soh-toh"]
  ];

  overrides.forEach(function(entry){
    spoken=spoken.replace(entry[0],entry[1]);
  });

  return spoken;
}

function normalizeRoadSpeech(value){
  return clean(value)
    .replace(/\bI[-\s]?(\d{1,3})\b/gi,"I $1")
    .replace(/\bUS[-\s]?(\d{1,3})\b/gi,"U S $1")
    .replace(/\bSR[-\s]?(\d{1,3})\b/gi,"State Route $1")
    .replace(/\bHwy\b\.?/gi,"Highway")
    .replace(/\bRd\b\.?/gi,"Road")
    .replace(/\bAve\b\.?/gi,"Avenue")
    .replace(/\bBlvd\b\.?/gi,"Boulevard")
    .replace(/\bPkwy\b\.?/gi,"Parkway");
}

function sanitizeSpeechText(value){
  var spoken=clean(value)
    .replace(/★/g,"")
    .replace(/&/g," and ")
    .replace(/\s+([,.!?])/g,"$1")
    .replace(/([.!?])(?=\S)/g,"$1 ")
    .replace(/\s+/g," ")
    .trim();

  spoken=normalizeRoadSpeech(spoken);
  spoken=applyPronunciationOverrides(spoken);

  return spoken;
}

function speechSupported(){
  return !!(
    window.speechSynthesis&&
    typeof window.SpeechSynthesisUtterance==="function"
  );
}

function listenButton(){
  var c=getCard();

  return c
    ? c.querySelector("[data-place-listen]")
    : null;
}

function updateListenButton(){
  var button=listenButton();
  var label;
  var icon;

  if(!button){return false;}

  label=button.querySelector("[data-listen-label]");
  icon=button.querySelector("[data-listen-icon]");

  button.classList.toggle("is-speaking",speaking);
  button.setAttribute(
    "aria-pressed",
    speaking?"true":"false"
  );
  button.setAttribute(
    "aria-label",
    speaking
      ? "Stop Place IQ voice"
      : "Listen to why Drive IQ recommends this place"
  );

  if(label){
    label.textContent=speaking?"STOP":"LISTEN";
  }

  if(icon){
    icon.textContent=speaking?"■":"🔊";
  }

  return true;
}

function stopSpeaking(reason){
  var wasSpeaking=speaking;

  speaking=false;
  spokenPlaceKey="";

  try{
    if(window.speechSynthesis){
      window.speechSynthesis.cancel();
    }
  }catch(error){}

  activeUtterance=null;
  updateListenButton();

  if(wasSpeaking){
    try{
      events.emit("place:voice-stopped",{
        reason:String(reason||"stopped")
      });
    }catch(error2){}
  }

  return wasSpeaking;
}

function spokenDistance(item){
  var miles=distanceMiles(item);
  var rounded;

  if(miles===null){return "";}

  if(miles<0.08){
    return "just ahead";
  }

  if(miles<0.2){
    return "less than a quarter mile away";
  }

  if(miles<0.38){
    return "about a quarter mile away";
  }

  if(miles<0.75){
    return "about half a mile away";
  }

  if(miles<1.25){
    return "about one mile away";
  }

  if(miles<1.75){
    return "about a mile and a half away";
  }

  rounded=Math.max(2,Math.round(miles));

  return "about "+spokenNumber(rounded)+" miles away";
}

function voiceSummary(item){
  var name=placeName(item);
  var reason=clean(reasonText(item));
  var distance=spokenDistance(item);
  var open=openText(item);
  var chips=decisionChips(item);
  var usefulChips=[];
  var category=categoryKey(item);
  var opener;
  var details=[];
  var event=eventRecord(item);
  var eventState=eventConfidence(item);
  var eventTime=eventTimeText(item);

  if(eventState==="confirmed_tonight"){
    details.push(
      eventTypeLabel(item)+
      (eventTime?" starts at "+eventTime:" is confirmed tonight")+
      (event&&event.floorOrArea
        ? " in "+clean(event.floorOrArea)
        : "")+
      "."
    );
  }else if(eventState==="upcoming_event"){
    details.push(
      "There is an upcoming "+eventTypeLabel(item).toLowerCase()+
      (eventTime?" at "+eventTime:"")+
      "."
    );
  }else if(eventState==="usually_happens_tonight"){
    details.push(
      clean(
        event&&event.recurrenceText||
        "This venue usually has live entertainment tonight."
      )
    );
  }else if(eventState==="known_music_venue"){
    details.push(
      "This is a known live music venue, but no current event was confirmed."
    );
  }

  chips.forEach(function(label){
    if(
      usefulChips.length<2&&
      !/very close|nearby|coffee match|top match|strong match|good match/i.test(label)
    ){
      usefulChips.push(label);
    }
  });

  opener=stableChoice(item,[
    "Drive IQ recommends "+name+" because ",
    name+" stands out because ",
    "This looks like a strong choice because "
  ],"voice-opener");

  if(reason){
    details.push(
      opener+
      reason.charAt(0).toLowerCase()+
      reason.slice(1)+
      "."
    );
  }else{
    details.push(
      "Drive IQ recommends "+name+" for this stop."
    );
  }

  var proof=socialProofText(item);

  if(proof){
    details.push(
      "It has "+proof.replace(/^★\s*/,"")+"."
    );
  }

  if(distance&&open&&/open/i.test(open)){
    details.push(
      "It is "+distance+" and open now."
    );
  }else if(distance){
    details.push("It is "+distance+".");
  }else if(open&&/open/i.test(open)){
    details.push("It is open now.");
  }else if(open&&/closed/i.test(open)){
    details.push("It is currently closed, so you may want to save it for later.");
  }

  if(usefulChips.length){
    details.push(
      stableChoice(item,[
        "A couple of useful highlights are ",
        "What helps this one stand out is ",
        "It also gives you "
      ],"voice-highlights")+
      usefulChips.join(" and ").toLowerCase()+
      "."
    );
  }else if(category==="food"&&isIconicPlace(item)){
    details.push(
      "It also gives you more of the local destination experience."
    );
  }

  return clean(details.join(" "));
}

function chooseVoice(){
  var voices=[];

  try{
    voices=window.speechSynthesis.getVoices()||[];
  }catch(error){
    voices=[];
  }

  return voices.find(function(voice){
    return /^en-US$/i.test(voice.lang)&&
      /samantha|ava|allison|serena|karen|female/i.test(voice.name);
  })||
  voices.find(function(voice){
    return /^en-US$/i.test(voice.lang);
  })||
  voices.find(function(voice){
    return /^en/i.test(voice.lang);
  })||
  null;
}

function speakPlace(){
  var item=validItem(activeItem);
  var summary;
  var utterance;
  var key;
  var voice;

  if(!item){return false;}

  if(!speechSupported()){
    try{
      events.emit("place:voice-unavailable",{
        item:item,
        reason:"speech-synthesis-unavailable"
      });
    }catch(error){}
    return false;
  }

  key=placeKey(item);

  if(speaking&&spokenPlaceKey===key){
    stopSpeaking("user-toggle");
    return true;
  }

  stopSpeaking("replace");

  summary=sanitizeSpeechText(voiceSummary(item));

  if(!summary){return false;}

  utterance=new window.SpeechSynthesisUtterance(sanitizeSpeechText(summary));
  voice=chooseVoice();

  utterance.lang=voice&&voice.lang
    ?voice.lang
    :"en-US";
  utterance.rate=0.90;
  utterance.pitch=1.02;
  utterance.volume=1;

  if(voice){
    utterance.voice=voice;
  }

  utterance.onstart=function(){
    if(activeUtterance!==utterance){return;}

    speaking=true;
    spokenPlaceKey=key;
    updateListenButton();

    try{
      events.emit("place:voice-started",{
        item:item,
        text:summary
      });
    }catch(error){}
  };

  utterance.onend=function(){
    if(activeUtterance!==utterance){return;}

    speaking=false;
    spokenPlaceKey="";
    activeUtterance=null;
    updateListenButton();

    try{
      events.emit("place:voice-finished",{
        item:item
      });
    }catch(error){}
  };

  utterance.onerror=function(event){
    if(activeUtterance!==utterance){return;}

    speaking=false;
    spokenPlaceKey="";
    activeUtterance=null;
    updateListenButton();

    try{
      events.emit("place:voice-failed",{
        item:item,
        error:String(
          event&&event.error
            ? event.error
            : "speech-error"
        )
      });
    }catch(error){}
  };

  activeUtterance=utterance;
  spokenPlaceKey=key;

  try{
    window.speechSynthesis.speak(utterance);
  }catch(error){
    activeUtterance=null;
    spokenPlaceKey="";
    speaking=false;
    updateListenButton();
    return false;
  }

  /*
    Some iOS builds delay the onstart callback. Reflect the user action
    immediately so the control never appears unresponsive.
  */
  speaking=true;
  updateListenButton();

  return true;
}

function galleryMarkup(item,gallery){
  var photo=gallery[0]||directPhoto(item);
  var dots="";
  var i;

  for(i=0;i<gallery.length;i+=1){
    dots+=
      '<span class="diqV600GalleryDot'+(i===0?" on":"")+'" '+
      'data-gallery-dot="'+i+'"></span>';
  }

  return (
    '<div class="diqV600Gallery" data-gallery-count="'+gallery.length+'" '+
      'data-gallery-multiple="'+(gallery.length>1?"1":"0")+'">'+
      '<div class="diq-v600-place-photo" data-diq-photo-hero="1" '+
        'data-photo-state="fallback"></div>'+
      (
        gallery.length>1
          ? '<button class="diqV600GalleryNav diqV600GalleryPrev" type="button" '+
              'data-gallery-prev="1" aria-label="Previous photo">&#8249;</button>'+
            '<button class="diqV600GalleryNav diqV600GalleryNext" type="button" '+
              'data-gallery-next="1" aria-label="Next photo">&#8250;</button>'
          : ''
      )+
      (
        gallery.length>1
          ? '<div class="diqV600GalleryMeta">'+
              '<div class="diqV600GalleryDots">'+dots+'</div>'+
              '<div class="diqV600GalleryCount" data-gallery-counter="1">1 / '+
                gallery.length+
              '</div>'+
            '</div>'
          : ''
      )+
    '</div>'
  );
}

/* V600.44 — Website IQ: optional, validated business website link. */
function placeWebsiteURL(item){
  var sources=[item,item&&item.details,item&&item.providerData,item&&item.raw,item&&item.properties];
  var keys=["websiteUri","websiteURL","websiteUrl","website","officialWebsite"];
  var i,j,source,value,url;
  for(i=0;i<sources.length;i+=1){
    source=sources[i];
    if(!source||typeof source!=="object"){continue;}
    for(j=0;j<keys.length;j+=1){
      value=source[keys[j]];
      if(!value||typeof value!=="string"){continue;}
      try{
        url=new window.URL(value.trim());
        if((url.protocol==="https:"||url.protocol==="http:")&&
           url.hostname&& !url.username && !url.password){
          return url.href;
        }
      }catch(error){}
    }
  }
  return "";
}

function render(value){
  var item=validItem(value);
  var c=getCard();
  var gallery;
  var chips;
  var decisionBar;
  var decisionChipsHTML;
  var verdictHTML;
  var socialProofHTML;
  var worthKnowingHTML;
  var reviewHighlightsHTML;
  var tonightIQHTML;
  var websiteURL;

  if(!c||!item){return false;}

  stopSpeaking("new-place-render");
  activeItem=item;
  galleryIndex=0;
  gallery=photoGallery(item);
  chips=buildChips(item);
  decisionBar=decisionBarMarkup(item);
  decisionChipsHTML=decisionChipsMarkup(item);
  verdictHTML=verdictMarkup(item);
  socialProofHTML=socialProofMarkup(item);
  worthKnowingHTML=worthKnowingMarkup(item);
  reviewHighlightsHTML=reviewHighlightsMarkup(item);
  tonightIQHTML=tonightIQMarkup(item);
  websiteURL=placeWebsiteURL(item);

  c.innerHTML=
    '<div class="diqV600PlaceCardInner">'+
      '<button id="'+escape(cfg.placeCard.closeButtonID)+'" '+
        'class="diqV600PlaceClose" type="button" aria-label="Close place card">&times;</button>'+
      galleryMarkup(item,gallery)+
      '<div class="diqV600PlaceBody">'+
        '<div class="diqV601PlaceEyebrowRow">'+
          '<div class="diqV600PlaceEyebrow">'+
            escape(categoryLabel(item))+
          '</div>'+
          '<div class="diqV601PlacePickBadge">DRIVE IQ PICK</div>'+
        '</div>'+
        '<h2 class="diqV600PlaceTitle">'+
          escape(item.label||item.name||"Unknown Place")+
        '</h2>'+
        '<div class="diqV600PlaceAddress">'+
          escape(item.address||item.formattedAddress||item.full||"")+
        '</div>'+
        verdictHTML+
        (decisionBar?'<div class="diqV600DecisionBar">'+decisionBar+'</div>':"")+
        tonightIQHTML+
        socialProofHTML+
        '<div class="diqV600PrimaryActions">'+
          '<button id="'+escape(cfg.placeCard.routeButtonID)+'" '+
            'class="diqV600RouteHere" type="button">ROUTE HERE</button>'+
          '<div class="diqV600QuickActions">'+
            '<button class="diqV600QuickAction" data-place-save="1" type="button">SAVE</button>'+
            '<button class="diqV600QuickAction" data-place-share="1" type="button">SHARE</button>'+
            '<button class="diqV600QuickAction" data-place-listen="1" type="button" '+
              'aria-pressed="false" aria-label="Listen to why Drive IQ recommends this place">'+
              '<span data-listen-icon="1">🔊</span> '+
              '<span data-listen-label="1">LISTEN</span>'+
            '</button>'+
          '</div>'+
          (websiteURL
            ? '<a class="diqV600WebsiteLink" href="'+escape(websiteURL)+'" '+
                'target="_blank" rel="noopener noreferrer external" '+
                'aria-label="Open this business website">🌐 VIEW WEBSITE ↗</a>'
            : '')+
        '</div>'+
        '<div class="diqV600WhyBlock">'+
          '<div class="diqV600WhyLabel">WHY DRIVE IQ PICKED THIS</div>'+
          '<div class="diqV600PlaceReason">'+
            escape(reasonText(item))+
          '</div>'+
          (decisionChipsHTML
            ? '<div class="diqV600DecisionChips">'+decisionChipsHTML+'</div>'
            : '')+
        '</div>'+
        worthKnowingHTML+
        reviewHighlightsHTML+
        (chips?'<div class="diqV600PlaceChips">'+chips+'</div>':"")+
      '</div>'+
    '</div>';

  c.__diqPlaceItem=item;
  c.__diqGallery=gallery.slice();
  c.classList.add("diqPlaceIQOpen");
  c.style.display="block";
  c.style.visibility="visible";
  c.style.opacity="1";
  c.style.pointerEvents="auto";
  c.setAttribute("aria-hidden","false");

  hydrateHeroSafely(item);

  return true;
}

function updateGallery(index){
  var c=getCard();
  var gallery;
  var hero;
  var counter;
  var dots;
  var targetURL;

  if(!c){return false;}

  gallery=c.__diqGallery||[];

  if(!gallery.length){return false;}

  galleryIndex=(Number(index)+gallery.length)%gallery.length;

  hero=c.querySelector("[data-diq-photo-hero]");
  counter=c.querySelector("[data-gallery-counter]");
  dots=c.querySelectorAll("[data-gallery-dot]");
  targetURL=clean(gallery[galleryIndex]);

  if(hero){
    /*
      Keep the fallback visible while the next gallery image is verified.
      A failed swipe must never replace a working visual with a black panel.
    */
    paintFallback(activeItem,hero);
    hero.style.opacity="0.88";

    if(targetURL){
      verifyAndCommitPhoto(activeItem,hero,targetURL,{commit:true});
    }
  }

  if(counter){
    counter.textContent=
      String(galleryIndex+1)+" / "+String(gallery.length);
  }

  Array.prototype.forEach.call(dots,function(dot,i){
    dot.classList.toggle("on",i===galleryIndex);
  });

  return true;
}

function shelfCandidates(){
  var selectors=[
    "#diqAdventureResultsShelf",
    "#diqV519Shelf",
    ".diqAdventureResultsShelf",
    ".diqV600ResultsShelf",
    "[data-diq-results-shelf]"
  ];
  var nodes=[];
  var seen=[];

  selectors.forEach(function(selector){
    var matches;

    try{
      matches=document.querySelectorAll(selector);
    }catch(error){
      matches=[];
    }

    Array.prototype.forEach.call(matches,function(node){
      if(node&&seen.indexOf(node)<0){
        seen.push(node);
        nodes.push(node);
      }
    });
  });

  return nodes;
}

function hideResultsShelf(){
  var shelves=shelfCandidates();

  hiddenShelfState=[];

  shelves.forEach(function(shelf){
    var stateSnapshot={
      node:shelf,
      display:shelf.style.display,
      visibility:shelf.style.visibility,
      opacity:shelf.style.opacity,
      pointerEvents:shelf.style.pointerEvents,
      ariaHidden:shelf.getAttribute("aria-hidden")
    };

    hiddenShelfState.push(stateSnapshot);

    shelf.classList.add("diqPlaceIQHiddenShelf");
    shelf.style.setProperty("display","none","important");
    shelf.style.setProperty("visibility","hidden","important");
    shelf.style.setProperty("opacity","0","important");
    shelf.style.setProperty("pointer-events","none","important");
    shelf.setAttribute("aria-hidden","true");
  });

  try{
    document.body.classList.add("diqPlaceIQMode");
  }catch(error){}

  try{
    events.emit("results:hidden-for-place",{
      count:shelves.length
    });
  }catch(error2){}

  return shelves.length;
}

function restoreResultsShelf(){
  try{
    document.body.classList.remove("diqPlaceIQMode");
  }catch(error){}

  hiddenShelfState.forEach(function(snapshot){
    var shelf=snapshot.node;

    if(!shelf){return;}

    shelf.classList.remove("diqPlaceIQHiddenShelf");
    shelf.style.removeProperty("display");
    shelf.style.removeProperty("visibility");
    shelf.style.removeProperty("opacity");
    shelf.style.removeProperty("pointer-events");

    if(snapshot.display){
      shelf.style.display=snapshot.display;
    }
    if(snapshot.visibility){
      shelf.style.visibility=snapshot.visibility;
    }
    if(snapshot.opacity){
      shelf.style.opacity=snapshot.opacity;
    }
    if(snapshot.pointerEvents){
      shelf.style.pointerEvents=snapshot.pointerEvents;
    }

    if(snapshot.ariaHidden===null){
      shelf.removeAttribute("aria-hidden");
    }else{
      shelf.setAttribute("aria-hidden",snapshot.ariaHidden);
    }
  });

  hiddenShelfState=[];

  try{
    if(
      window.DriveIQAdventureScan&&
      typeof window.DriveIQAdventureScan.renderResultShelf==="function"
    ){
      window.DriveIQAdventureScan.renderResultShelf(
        state.get("results.items",[])
      );
    }
  }catch(error2){}

  try{
    events.emit("results:restored-after-place",{});
  }catch(error3){}
}


function cardIsVisiblyOpen(){
  var c=getCard();

  return !!(
    c&&
    c.classList.contains("diqPlaceIQOpen")&&
    c.style.display!=="none"&&
    c.getAttribute("aria-hidden")!=="true"&&
    c.querySelector(".diqV600PlaceCardInner")&&
    c.querySelector(".diqV600PlaceTitle")
  );
}

function rollbackOpen(item,source,error){
  var c=getCard();

  opening=false;
  clearHeroTimers();
  stopSpeaking("open-rollback");

  if(c){
    c.classList.remove("diqPlaceIQOpen");
    c.style.display="none";
    c.style.visibility="hidden";
    c.style.opacity="0";
    c.style.pointerEvents="none";
    c.setAttribute("aria-hidden","true");
  }

  try{
    document.body.classList.remove("diqPlaceIQMode");
    document.body.classList.remove("diq-v600-place");
  }catch(bodyError){}

  try{
    if(typeof state.setUIState==="function"){
      state.setUIState("browse_results");
    }
  }catch(stateError){}

  restoreResultsShelf();

  try{
    if(
      window.DriveIQAdventureUI&&
      typeof window.DriveIQAdventureUI.showOnlyResultShelf==="function"
    ){
      window.DriveIQAdventureUI.showOnlyResultShelf();
    }
  }catch(uiError){}

  try{
    mapApi.restore("place-open-rollback");
  }catch(mapError){}

  try{
    events.emit("place:open-failed",{
      item:item||null,
      source:String(source||"place-card-direct"),
      message:String(
        error&&error.message
          ? error.message
          : error||"Place IQ could not open"
      )
    });
  }catch(eventError){}

  return false;
}

function open(value,source){
  var incoming=validItem(value);
  var item;
  var rendered=false;
  var focused=true;
  var token;

  if(!incoming){return false;}

  /*
    Capture the selected place once. From this point forward render, Photo IQ,
    state, camera, Share IQ, Listen, and Route Here use the same snapshot.
  */
  item=cloneSelectionItem(incoming);
  token=selectionToken(item,source||"place-card-direct");
  item.__diqSelectionToken=token;
  committedSelectionByToken[token]=item;

  /*
    A delayed duplicate event for the same place is harmless. Never let a
    stale opening flag permanently block later pin taps.
  */
  if(opening){
    window.setTimeout(function(){
      if(
        !activeItem||
        activeSelectionToken!==token
      ){
        opening=false;
        open(item,source);
      }
    },80);
    return false;
  }

  if(!getCard()){
    activeItem=item;
    activeSelectionToken=token;
    scheduleInitRetry();

    window.setTimeout(function(){
      if(
        activeItem&&
        activeSelectionToken===token&&
        getCard()
      ){
        open(item,source);
      }
    },140);

    return false;
  }

  opening=true;
  activeItem=item;
  activeSelectionToken=token;

  try{
    /*
      Transaction Step 1:
      Render and verify the complete card before hiding any existing results.
    */
    rendered=render(item);

    if(!rendered||!cardIsVisiblyOpen()){
      return rollbackOpen(
        item,
        source,
        new Error("Place card did not render visibly.")
      );
    }

    /*
      Transaction Step 2:
      Preserve exact place identity. A state failure must not strand the UI.
    */
    try{
      state.selectPlace(item,source||"place-card-direct");
    }catch(stateError){
      return rollbackOpen(item,source,stateError);
    }

    /*
      Transaction Step 3:
      Camera movement is optional. A failed camera ease must never prevent
      Place IQ from opening or blank the map.
    */
    try{
      if(
        mapApi&&
        typeof mapApi.focusPlace==="function"
      ){
        focused=mapApi.focusPlace(item,{
          duration:450,
          zoom:(cfg.map&&cfg.map.placeZoom)||15.2
        });
      }
    }catch(mapError){
      focused=false;
    }

    if(focused===false){
      try{
        mapApi.restore("place-focus-fallback");
      }catch(restoreError){}
    }

    /*
      Transaction Step 4:
      Only after the card is visibly ready do we hide the result shelf and
      enter Place IQ mode.
    */
    hideResultsShelf();

    window.setTimeout(function(){
      if(cardIsVisiblyOpen()){
        hideResultsShelf();
      }
    },0);

    window.setTimeout(function(){
      if(cardIsVisiblyOpen()){
        hideResultsShelf();
      }
    },120);

    try{
      if(typeof state.setUIState==="function"){
        state.setUIState("place");
      }
    }catch(uiStateError){
      return rollbackOpen(item,source,uiStateError);
    }

    try{
      events.emit("place:opened",{
        item:item,
        source:source||"place-card-direct"
      });
    }catch(eventError){
      return rollbackOpen(item,source,eventError);
    }

    /*
      Final verification catches delayed class/style collisions.
    */
    window.setTimeout(function(){
      if(
        activeItem&&
        placeKey(activeItem)===placeKey(item)&&
        !cardIsVisiblyOpen()
      ){
        rollbackOpen(
          item,
          source,
          new Error("Place card was hidden after opening.")
        );
      }
    },180);

    opening=false;
    return true;
  }catch(error){
    return rollbackOpen(item,source,error);
  }
}

function close(){
  var c=getCard();
  var closedItem=activeItem;

  /*
    Close Authority:
    clear every local gate that could suppress the next physical pin tap.
    The proven V600.25 open path remains unchanged.
  */
  opening=false;
  clearHeroTimers();
  stopSpeaking("place-close");

  if(c){
    c.classList.remove("diqPlaceIQOpen");
    c.style.display="none";
    c.style.visibility="hidden";
    c.style.opacity="0";
    c.style.pointerEvents="none";
    c.setAttribute("aria-hidden","true");
    c.__diqPlaceItem=null;
    c.__diqGallery=[];
  }

  activeItem=null;
  activeSelectionToken="";
  galleryIndex=0;
  touchStartX=null;
  touchStartY=null;

  try{
    document.body.classList.remove("diqPlaceIQMode");
    document.body.classList.remove("diq-v600-place");
  }catch(error){}

  try{state.closePlace();}catch(error2){}

  try{
    if(typeof state.setUIState==="function"){
      state.setUIState("map");
    }
  }catch(error3){}

  try{
    events.emit("place:closed",{
      item:closedItem,
      source:"place-card-close",
      reason:"user-close"
    });
  }catch(error4){}

  restoreResultsShelf();

  window.setTimeout(function(){
    var m;

    try{
      m=mapApi.getMap&&mapApi.getMap();

      if(m){
        if(m.dragPan){m.dragPan.enable();}
        if(m.touchZoomRotate){m.touchZoomRotate.enable();}
        if(m.scrollZoom){m.scrollZoom.enable();}
      }
    }catch(error){}

    /*
      Do not force a source repaint here. A repaint can rebuild Mapbox layer
      state while the user's next tap begins. The committed pin layer remains
      intact and clickable.
    */
  },0);

  return true;
}

function routeSelectedPlace(){
  var c=getCard();
  var item=
    activeItem||
    (c&&c.__diqPlaceItem)||
    state.get("placeCard.item",null)||
    state.get("selectedPlace",null);
  var routeAuthority;

  item=validItem(item);

  if(!item){return false;}

  stopSpeaking("route-handoff");

  /*
   * V600.42 Route Button Authority:
   * Do not call state.requestRoute() and then emit route:requested. That creates
   * two independent route lanes, allowing an older listener/native shell path
   * to navigate to Home before the current Adventure Route authority runs.
   *
   * Route Here now calls the one installed route authority directly.
   */
  routeAuthority=window.DriveIQAdventureRoute;

  if(
    routeAuthority&&
    typeof routeAuthority.startRouteTo==="function"
  ){
    return routeAuthority.startRouteTo(item);
  }

  /*
   * Fail closed when the route authority has not loaded. Never fall through to
   * a competing legacy navigation handler.
   */
  try{
    state.merge("route",{
      active:false,
      requested:false,
      error:"Route authority is not ready."
    },{
      action:"route:authority-missing"
    });
  }catch(error){}

  try{
    events.emit("route:failed",{
      message:"Route authority is not ready.",
      item:item,
      timestamp:Date.now()
    });
  }catch(error2){}

  return false;
}

function favoriteItems(){
  try{
    var parsed=JSON.parse(localStorage.getItem(FAVORITES_KEY)||"[]");
    return Array.isArray(parsed)?parsed:[];
  }catch(error){
    return [];
  }
}

function savePlace(){
  var item=validItem(activeItem);
  var list;
  var key;

  if(!item){return false;}

  list=favoriteItems();
  key=placeKey(item);

  if(!list.some(function(candidate){
    return placeKey(candidate)===key;
  })){
    list.push(item);

    try{
      localStorage.setItem(
        FAVORITES_KEY,
        JSON.stringify(list)
      );
    }catch(error){}
  }

  try{
    events.emit("place:saved",item);
  }catch(error2){}

  return true;
}

function canonicalPlaceId(item){
  return clean(
    item&&(
      item.placeId||
      item.place_id||
      item.googlePlaceId||
      item.google_place_id||
      item.providerPlaceId||
      item.id||
      ""
    )
  );
}

function sharedPlaceName(item){
  return clean(
    item&&(
      item.canonicalName||
      item.displayName||
      item.label||
      item.name
    )||
    "Shared Place"
  );
}

function sharedPlaceAddress(item){
  return clean(
    item&&(
      item.formattedAddress||
      item.fullAddress||
      item.address||
      item.full||
      item.vicinity||
      ""
    )
  );
}

function shareCoordinate(item,key){
  var value;

  if(!item){return null;}

  value=key==="lat"
    ?(
      item.lat!=null
        ?item.lat
        :item.latitude
    )
    :(
      item.lng!=null
        ?item.lng
        :item.longitude
    );

  value=Number(value);

  return isFinite(value)
    ?value
    :null;
}

function shareBaseURL(){
  var configured=clean(
    cfg&&cfg.share&&(
      cfg.share.placeBaseURL||
      cfg.share.baseURL
    )
  );

  if(configured){return configured;}

  return "https://driveiqgps.com/pages/start-drive";
}

function buildSharedPlaceLink(item){
  var placeId=canonicalPlaceId(item);
  var name=sharedPlaceName(item);
  var address=sharedPlaceAddress(item);
  var lat=shareCoordinate(item,"lat");
  var lng=shareCoordinate(item,"lng");
  var params=new URLSearchParams();

  params.set(
    "share",
    placeId
      ?"place"
      :"pin"
  );
  params.set("name",name);

  if(placeId){
    params.set("placeId",placeId);
  }

  if(lat!==null){
    params.set("lat",String(lat));
  }

  if(lng!==null){
    params.set("lng",String(lng));
  }

  if(address){
    params.set("address",address);
  }

  params.set("source","place-iq");

  return shareBaseURL()+"?"+params.toString();
}

function shareMessageForPlace(item){
  return canonicalPlaceId(item)
    ?"Meet me at "+sharedPlaceName(item)+"."
    :"Meet me here.";
}

function copyShareText(value){
  var textValue=String(value||"");

  if(
    navigator.clipboard&&
    typeof navigator.clipboard.writeText==="function"
  ){
    return navigator.clipboard.writeText(textValue);
  }

  return new Promise(function(resolve,reject){
    var area=document.createElement("textarea");

    area.value=textValue;
    area.setAttribute("readonly","");
    area.style.position="fixed";
    area.style.left="-9999px";
    area.style.opacity="0";

    document.body.appendChild(area);
    area.select();

    try{
      if(document.execCommand("copy")){
        resolve(true);
      }else{
        reject(new Error("Copy command failed"));
      }
    }catch(error){
      reject(error);
    }finally{
      if(area.parentNode){
        area.parentNode.removeChild(area);
      }
    }
  });
}

function emitPlaceShared(item,link,method){
  try{
    events.emit("place:shared",{
      item:item,
      place:item,
      link:link,
      method:method,
      shareType:canonicalPlaceId(item)
        ?"place"
        :"pin"
    });
  }catch(error){}
}

function showShareToast(message){
  try{
    if(
      window.DriveIQAdventureUI&&
      typeof window.DriveIQAdventureUI.showToast==="function"
    ){
      window.DriveIQAdventureUI.showToast(message);
    }
  }catch(error){}
}

function sharePlace(){
  var item=validItem(activeItem);
  var link;
  var message;
  var payload;

  if(!item){
    showShareToast("This place is not ready to share.");
    return false;
  }

  link=buildSharedPlaceLink(item);
  message=shareMessageForPlace(item);
  payload={
    title:sharedPlaceName(item),
    text:message,
    url:link
  };

  if(
    navigator.share&&
    typeof navigator.share==="function"
  ){
    navigator.share(payload)
      .then(function(){
        emitPlaceShared(
          item,
          link,
          "native-share"
        );
      })
      .catch(function(error){
        if(
          error&&
          (
            error.name==="AbortError"||
            error.name==="NotAllowedError"
          )
        ){
          return;
        }

        copyShareText(
          message+"\n"+link
        ).then(function(){
          emitPlaceShared(
            item,
            link,
            "clipboard-fallback"
          );
          showShareToast("Place link copied");
        }).catch(function(){
          showShareToast(
            "Could not copy the place link"
          );
        });
      });

    return true;
  }

  copyShareText(
    message+"\n"+link
  ).then(function(){
    emitPlaceShared(
      item,
      link,
      "clipboard"
    );
    showShareToast("Place link copied");
  }).catch(function(){
    showShareToast(
      "Could not copy the place link"
    );
  });

  return true;
}

function handleClick(event){
  var target=event.target;
  var c=getCard();

  if(!c||!target||!c.contains(target)){return;}

  event.stopPropagation();

  if(target.closest&&target.closest("#"+cfg.placeCard.closeButtonID)){
    event.preventDefault();
    event.stopImmediatePropagation();
    close();
    return;
  }

  if(target.closest&&target.closest("[data-gallery-prev]")){
    event.preventDefault();
    event.stopImmediatePropagation();
    updateGallery(galleryIndex-1);
    return;
  }

  if(target.closest&&target.closest("[data-gallery-next]")){
    event.preventDefault();
    event.stopImmediatePropagation();
    updateGallery(galleryIndex+1);
    return;
  }

  if(target.closest&&target.closest("[data-gallery-dot]")){
    event.preventDefault();
    event.stopImmediatePropagation();
    updateGallery(
      Number(
        target.closest("[data-gallery-dot]")
          .getAttribute("data-gallery-dot")
      )
    );
    return;
  }

  if(target.closest&&target.closest("[data-place-listen]")){
    event.preventDefault();
    event.stopImmediatePropagation();
    speakPlace();
    return;
  }

  if(target.closest&&target.closest("[data-place-save]")){
    event.preventDefault();
    event.stopImmediatePropagation();
    savePlace();
    target.closest("[data-place-save]").textContent="SAVED";
    return;
  }

  if(target.closest&&target.closest("[data-place-share]")){
    event.preventDefault();
    event.stopImmediatePropagation();
    sharePlace();
    return;
  }

  if(target.closest&&target.closest("#"+cfg.placeCard.routeButtonID)){
    event.preventDefault();
    event.stopImmediatePropagation();
    routeSelectedPlace();
  }
}

function handleTouchStart(event){
  var touch=event.touches&&event.touches[0];

  if(!touch){return;}

  touchStartX=touch.clientX;
  touchStartY=touch.clientY;
}

function handleTouchEnd(event){
  var touch=event.changedTouches&&event.changedTouches[0];
  var dx;
  var dy;

  if(
    !touch||
    touchStartX===null||
    touchStartY===null
  ){
    return;
  }

  dx=touch.clientX-touchStartX;
  dy=touch.clientY-touchStartY;

  touchStartX=null;
  touchStartY=null;

  if(Math.abs(dx)<45||Math.abs(dx)<Math.abs(dy)){
    return;
  }

  updateGallery(
    dx<0
      ? galleryIndex+1
      : galleryIndex-1
  );
}

function openFromSelectionEvent(event,source){
  var item=validItem(unwrapItem(event));
  var snapshot;

  /*
    Only fall back to state when the event truly contains no usable place.
    Never replace a valid tapped object with the currently selected place.
  */
  if(!item){
    item=validItem(
      state.get("placeCard.item",null)||
      state.get("selectedPlace",null)||
      state.get("place.selected",null)
    );
  }

  if(!item){return false;}

  snapshot=cloneSelectionItem(item);

  if(!getCard()){
    activeItem=snapshot;
    scheduleInitRetry();
  }

  return open(snapshot,source||"selection-event");
}

function installEventBindings(){
  if(eventBindingsInstalled){return true;}

  /*
    Pin taps must always bridge into Place IQ, regardless of whether the
    event bus supplies a raw result, a nested payload, or a Mapbox feature.
  */
  events.on("pin:selected",function(event){
    openFromSelectionEvent(event,"pin-selected-event");
  });

  events.on("result:selected",function(event){
    openFromSelectionEvent(event,"result-selected-event");
  });

  events.on("place:selected",function(event){
    openFromSelectionEvent(event,"place-selected-event");
  });

  eventBindingsInstalled=true;
  return true;
}

function scheduleInitRetry(){
  if(
    initialized||
    initRetryTimer||
    initRetryCount>=MAX_INIT_RETRIES
  ){
    return;
  }

  initRetryTimer=window.setTimeout(function(){
    initRetryTimer=null;
    initRetryCount+=1;
    init();

    if(!initialized){
      scheduleInitRetry();
    }
  },100);
}

function init(){
  if(initialized){return true;}

  ensureStyles();
  ensurePremiumPlaceStyles();
  installEventBindings();

  card=getCard();

  if(!card){
    scheduleInitRetry();
    return false;
  }

  card.addEventListener("click",handleClick,true);
  card.addEventListener("touchstart",handleTouchStart,{passive:true});
  card.addEventListener("touchend",handleTouchEnd,{passive:true});

  document.addEventListener("visibilitychange",function(){
    if(document.hidden){
      stopSpeaking("page-hidden");
    }
  });

  window.addEventListener("pagehide",function(){
    stopSpeaking("page-hide");
  });

  card.style.display="none";
  card.style.visibility="hidden";
  card.style.opacity="0";
  card.style.pointerEvents="none";
  card.setAttribute("aria-hidden","true");

  initialized=true;

  if(initRetryTimer){
    window.clearTimeout(initRetryTimer);
    initRetryTimer=null;
  }

  return true;
}

window.DriveIQAdventurePlaceCard={
  init:init,
  open:open,
  close:close,
  render:render,
  routeSelectedPlace:routeSelectedPlace,
  savePlace:savePlace,
  sharePlace:sharePlace,
  speakPlace:speakPlace,
  stopSpeaking:stopSpeaking,
  voiceSummary:voiceSummary,
  buildSharedPlaceLink:buildSharedPlaceLink,
  sharePlace:sharePlace,
  canonicalPlaceId:canonicalPlaceId,
  spokenDistance:spokenDistance,
  sanitizeSpeechText:sanitizeSpeechText,
  applyPronunciationOverrides:applyPronunciationOverrides,
  companionHeadline:companionHeadline,
  driveIQVerdict:driveIQVerdict,
  socialProofText:socialProofText,
  reviewCountValue:reviewCountValue,
  worthKnowingItems:worthKnowingItems,
  reviewHighlightItems:reviewHighlightItems,
  eventRecord:eventRecord,
  tonightIQMarkup:tonightIQMarkup,
  updateGallery:updateGallery,
  openFromSelectionEvent:openFromSelectionEvent,
  current:function(){return activeItem;},
  gallery:function(){
    var c=getCard();
    return c&&Array.isArray(c.__diqGallery)
      ? c.__diqGallery.slice()
      : [];
  },
  status:function(){
    return {
      build:"V600_43_PREMIUM_PLACE_IQ_PRESENTATION",
      initialized:initialized,
      currentPlaceKey:activeItem?placeKey(activeItem):"",
      activeSelectionToken:activeSelectionToken,
      immutableSelectionAuthority:true,
      eventBindingsInstalled:eventBindingsInstalled,
      initRetryCount:initRetryCount,
      hiddenShelfCount:hiddenShelfState.length,
      exclusivePlaceMode:
        !!(document.body&&document.body.classList.contains("diqPlaceIQMode")),
      open:!!activeItem,
      speechSupported:speechSupported(),
      speaking:speaking,
      spokenPlaceKey:spokenPlaceKey,
      galleryIndex:galleryIndex,
      galleryCount:
        getCard()&&Array.isArray(getCard().__diqGallery)
          ? getCard().__diqGallery.length
          : 0
    };
  }
};

if(document.readyState==="loading"){
  document.addEventListener("DOMContentLoaded",init,{once:true});
}else{
  init();
}

})(window,document);
